import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { saveAs } from "file-saver";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { Icon } from "@/components/Icon";
import { Tabs } from "@/components/Tabs";
import { Badge } from "@/components/Badge";
import { Switch } from "@/components/Switch";
import { Alert } from "@/components/Alert";
import { Modal } from "@/components/Modal";
import { Input } from "@/components/Input";
import { SearchInput } from "@/components/SearchInput";
import { TruncatedTooltipText } from "@/components/TruncatedTooltipText";
import { developmentApi, taskStatusesApi } from "@/lib/api";
import {
    DevelopmentParticipants,
    type ParticipantItem,
} from "@/pages/Developments/DevelopmentParticipants";
import { Select, type SelectOption } from "@/components/Select";
import {
    ImageLightboxModal,
    type LightboxItem,
    isImageFile,
} from "@/components/ImageLightboxModal";
import { FileViewer } from "@/components/FileViewer";
import {
    TicketsSection,
    parseTicketsList,
    type TicketItem,
} from "@/pages/Developments/TicketsSection";
import { getAttachmentFileIcon } from "@/utils/attachmentFileIcon";
import { ATTACHMENT_DRAG_MIME } from "@/utils/attachmentReference";
import {
    date,
    formatAttachmentMeta,
    formatFileSize,
    getInitials,
} from "@/utils/format";
import { SuggestTasksModal } from "@/pages/Developments/SuggestTasksModal";
import { Tooltip } from "@/components/Tooltip";
import { ChangeTaskStatusModal } from "@/pages/Tasks/ChangeTaskStatusModal";
import { ChangeTaskAssigneeModal } from "@/pages/Tasks/ChangeTaskAssigneeModal";
import { NewTaskModal } from "@/pages/Tasks/NewTaskModal";
import { type BadgeVariant } from "@/components/Badge";

const TASK_STATUS_VARIANT: Record<string, BadgeVariant> = {
    todo: "neutral",
    progress: "info",
    review: "warning",
    done: "success",
    cancelled: "danger",
};

const getInternalNotePreview = (content?: string): string => {
    const plain = (content ?? "")
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/p>/gi, "\n")
        .replace(/<[^>]+>/g, "")
        .replace(/\s+/g, " ")
        .trim();

    return (
        plain
            .split("\n")
            .map((line) => line.trim())
            .find(Boolean) ?? ""
    );
};

interface Attachment {
    id: number;
    name: string;
    file_size: number;
    mime_type?: string;
    category?: string | null;
    requirement_attachment_id?: number | null;
    requirement_attachment_name?: string | null;
    is_internal?: boolean;
    message_id?: number | null;
    created_at?: string;
    user_id?: number;
    user?: { id?: number; name: string };
}

interface DeliverablesPanelProps {
    development: {
        id: number;
        client_id?: number;
        design_url?: string | null;
        figma_url?: string | null;
        tickets?: TicketItem[];
        metadata?: {
            tickets?: TicketItem[];
            jira_tasks?: TicketItem[];
            tasks?: TicketItem[];
            [key: string]: unknown;
        };
        attachments?: Attachment[];
        participants?: ParticipantItem[];
        messages?: Array<{
            id: number;
            content?: string;
            sender_name?: string;
            created_at: string;
            is_internal?: boolean;
            type?: string;
            attachments?: Array<{ id: number }>;
        }>;
    };
    onUpdate: (data?: unknown) => void;
    onRefresh?: () => void;
    onNavigateToMessage?: (messageId: number) => void;
    isInPhaseOne?: boolean;
    isInDesignPhase?: boolean;
    isInFrontendPhase?: boolean;
    isInBackendPhase?: boolean;
    onOpenDesignModal?: (requirementDocId?: number) => void;
}

const FILE_CATEGORIES = [
    "requirements",
    "design",
    "frontend",
    "backend",
    "documentation",
    "image",
    "ai",
    "other",
];

interface TechnicalLink {
    id: string;
    name: string;
    url: string;
    user_id?: number;
    is_internal?: boolean;
}

const isTechnicalLink = (value: unknown): value is TechnicalLink => {
    if (!value || typeof value !== "object") {
        return false;
    }
    const item = value as Record<string, unknown>;
    return (
        typeof item.id === "string" &&
        typeof item.name === "string" &&
        typeof item.url === "string"
    );
};

const parseTechnicalLinks = (
    development: DeliverablesPanelProps["development"]
): TechnicalLink[] => {
    const raw = development.metadata?.technical_links;
    if (Array.isArray(raw)) {
        const parsed = raw
            .filter(isTechnicalLink)
            .map((link) => {
                const rawObj = link as unknown as Record<string, unknown>;
                return {
                    id: link.id,
                    name: link.name.trim(),
                    url: link.url.trim(),
                    user_id:
                        typeof rawObj.user_id === "number"
                            ? rawObj.user_id
                            : undefined,
                    is_internal: Boolean(rawObj.is_internal),
                };
            })
            .filter((link) => link.url);

        if (parsed.length > 0) {
            return parsed;
        }
    }

    const legacyUrl = (
        development.design_url ??
        development.figma_url ??
        ""
    ).trim();
    if (!legacyUrl) {
        return [];
    }

    const legacyName =
        typeof development.metadata?.design_link_name === "string"
            ? development.metadata.design_link_name.trim()
            : "";

    return [
        {
            id: "legacy",
            name: legacyName || legacyUrl,
            url: legacyUrl,
        },
    ];
};

type PanelSectionKey =
    | "attachments"
    | "design"
    | "technicalLinks"
    | "internalNotes"
    | "internalTasks";

const matchesPanelSearch = (
    needle: string,
    ...parts: Array<string | undefined | null>
): boolean => {
    const normalized = needle.trim().toLocaleLowerCase("es");
    if (!normalized) {
        return true;
    }
    const haystack = parts.filter(Boolean).join(" ").toLocaleLowerCase("es");
    return haystack.includes(normalized);
};

interface PanelSectionProps {
    icon: string;
    iconClassName?: string;
    title: string;
    count: number;
    isOpen: boolean;
    onToggle: () => void;
    children: React.ReactNode;
    className?: string;
    sectionProps?: React.ComponentProps<"section">;
}

const PanelSection: React.FC<PanelSectionProps> = ({
    icon,
    iconClassName = "text-primary",
    title,
    count,
    isOpen,
    onToggle,
    children,
    className = "",
    sectionProps,
}) => (
    <section
        className={`bg-box rounded-2xl border border-slate-200/80 p-5 shadow-xs ${className}`}
        {...sectionProps}
    >
        <button
            type="button"
            onClick={onToggle}
            className="flex w-full items-center justify-between cursor-pointer select-none"
            aria-expanded={isOpen}
        >
            <div className="flex min-w-0 items-center gap-2">
                <Icon value={icon} size={17} className={iconClassName} />
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900">
                    {title}
                </h3>
            </div>
            <div className="flex shrink-0 items-center gap-2">
                <Badge variant="primary" size="sm" count>
                    {count}
                </Badge>
                <Icon
                    value="chevronRight"
                    size={14}
                    className={`text-slate-400 transition-transform duration-300 ease-in-out ${isOpen ? "rotate-90" : ""}`}
                />
            </div>
        </button>

        <div
            className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
        >
            <div className="overflow-hidden">
                <div className="space-y-3 pt-3">{children}</div>
            </div>
        </div>
    </section>
);

interface FileCategoryAccordionProps {
    label: string;
    icon: string;
    count: number;
    isOpen: boolean;
    onToggle: () => void;
    children: React.ReactNode;
}

const FileCategoryAccordion: React.FC<FileCategoryAccordionProps> = ({
    label,
    icon,
    count,
    isOpen,
    onToggle,
    children,
}) => (
    <div className="overflow-hidden rounded-xl border border-slate-200/80 bg-slate-50/50 shadow-xs dark:border-dark-border dark:bg-dark-subtle/40">
        <button
            type="button"
            onClick={onToggle}
            className="flex w-full items-center justify-between gap-2 px-3 py-2.5 cursor-pointer select-none transition-colors hover:bg-slate-100/90 dark:hover:bg-white/5"
            aria-expanded={isOpen}
        >
            <div className="flex min-w-0 items-center gap-2">
                <Icon
                    value="chevronRight"
                    size={14}
                    className={`shrink-0 text-slate-400 transition-transform duration-300 ease-in-out ${isOpen ? "rotate-90" : ""}`}
                />
                <Icon
                    value={icon}
                    size={15}
                    className="shrink-0 text-primary"
                />
                <span className="truncate text-[11px] font-bold uppercase tracking-wider text-slate-800 dark:text-zinc-200">
                    {label}
                </span>
            </div>
            <Badge variant="neutral" size="sm" count>
                {count}
            </Badge>
        </button>

        <div
            className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
        >
            <div className="overflow-hidden">
                <div className="space-y-1.5 border-t border-slate-200/70 px-2 py-2 dark:border-white/10">
                    {children}
                </div>
            </div>
        </div>
    </div>
);

const AttachmentFilePreview = ({
    developmentId,
    attachment,
    fallbackIcon,
    fallbackColor,
}: {
    developmentId: number;
    attachment: { id: number; name: string; mime_type?: string };
    fallbackIcon: string;
    fallbackColor: string;
}) => {
    const [url, setUrl] = useState<string | null>(null);

    useEffect(() => {
        if (!isImageFile(attachment.name, attachment.mime_type)) {
            return;
        }

        let active = true;
        let objectUrl = "";

        developmentApi
            .getAttachmentBlob(developmentId, attachment.id)
            .then((blob) => {
                if (!active) {
                    return;
                }

                objectUrl = URL.createObjectURL(blob);
                setUrl(objectUrl);
            })
            .catch(() => undefined);

        return () => {
            active = false;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [developmentId, attachment.id, attachment.name, attachment.mime_type]);

    if (url) {
        return (
            <img
                src={url}
                alt=""
                className="h-9 w-9 shrink-0 rounded-md object-cover"
            />
        );
    }

    return (
        <span className={`shrink-0 ${fallbackColor}`}>
            <Icon value={fallbackIcon} size={24} />
        </span>
    );
};

export const DeliverablesPanel: React.FC<DeliverablesPanelProps> = ({
    development,
    onUpdate,
    onRefresh,
    onNavigateToMessage,
    isInPhaseOne = false,
    isInDesignPhase = false,
    isInFrontendPhase = false,
    isInBackendPhase = false,
    onOpenDesignModal,
}: DeliverablesPanelProps) => {
    const { t } = useTranslation("global");
    const { user, isIt, isClient, isDevelopment } = useAuth();
    const { showSuccess } = useToast();
    const currentParticipant = development.participants?.find(
        (p) => p.user_id === user?.id
    );
    const isCancelled = development.status === "cancelled";
    const isEditor =
        !isCancelled &&
        (currentParticipant
            ? currentParticipant.role === "editor"
            : isIt || isDevelopment || development.client_id === user?.id);

    const [technicalLinksOpen, setTechnicalLinksOpen] = useState(true);
    const [documentsOpen, setDocumentsOpen] = useState(true);
    const [designOpen, setDesignOpen] = useState(true);
    const [frontendOpen, setFrontendOpen] = useState(true);
    const [backendOpen, setBackendOpen] = useState(true);
    const [activeDesignTab, setActiveDesignTab] = useState<"files" | "links">(
        "files"
    );
    const [filesOpen, setFilesOpen] = useState(true);
    const fileCategoryOptions: SelectOption<string>[] = [
        {
            value: "requirements",
            label: t("developments.detail.file_categories.requirements"),
            icon: "fileText",
        },
        {
            value: "design",
            label: t("developments.detail.file_categories.design"),
            icon: "layers",
        },
        {
            value: "frontend",
            label: t("developments.detail.file_categories.frontend"),
            icon: "monitor",
        },
        {
            value: "backend",
            label: t("developments.detail.file_categories.backend"),
            icon: "server",
        },
        {
            value: "documentation",
            label: t("developments.detail.file_categories.documentation"),
            icon: "folder",
        },
        {
            value: "image",
            label: t("developments.detail.file_categories.image"),
            icon: "image",
        },
        {
            value: "ai",
            label: t("developments.detail.file_categories.ai"),
            icon: "sparkles",
        },
        {
            value: "other",
            label: t("developments.detail.file_categories.other"),
            icon: "paperclip",
        },
    ];
    const [activeFileTab, setActiveFileTab] = useState<
        "uploaded" | "chat" | "ai"
    >("uploaded");
    const [uploading, setUploading] = useState<boolean>(false);
    const [isInternalFile, setIsInternalFile] = useState<boolean>(false);
    const [generatingDocument, setGeneratingDocument] =
        useState<boolean>(false);
    const [documentError, setDocumentError] = useState<string | null>(null);
    const [activeReqDocTab, setActiveReqDocTab] = useState<
        "client" | "internal"
    >("client");
    const [activeDropdownId, setActiveDropdownId] = useState<number | null>(
        null
    );
    const [activeTaskDropdownId, setActiveTaskDropdownId] = useState<
        number | null
    >(null);
    const [dropdownCoords, setDropdownCoords] = useState<{
        top?: number;
        bottom?: number;
        right: number;
    } | null>(null);

    useEffect(() => {
        if (activeDropdownId !== null || activeTaskDropdownId !== null) {
            const close = () => {
                setActiveDropdownId(null);
                setActiveTaskDropdownId(null);
            };
            window.addEventListener("click", close);
            window.addEventListener("scroll", close, true);
            return () => {
                window.removeEventListener("click", close);
                window.removeEventListener("scroll", close, true);
            };
        }
    }, [activeDropdownId, activeTaskDropdownId]);

    const clientRequirementDocs = useMemo(
        () =>
            (development.attachments ?? []).filter(
                (file) =>
                    file.category === "requirements_document" &&
                    (!file.is_internal ||
                        file.name.toLowerCase().endsWith(".pdf"))
            ),
        [development.attachments]
    );
    const internalRequirementDocs = useMemo(
        () =>
            (development.attachments ?? []).filter(
                (file) =>
                    file.category === "requirements_document" &&
                    (file.is_internal ||
                        file.name.toLowerCase().endsWith(".md"))
            ),
        [development.attachments]
    );

    const designFiles = useMemo(
        () =>
            (development.attachments ?? []).filter(
                (file) => file.category?.toLowerCase() === "design"
            ),
        [development.attachments]
    );

    const frontendFiles = useMemo(
        () =>
            (development.attachments ?? []).filter(
                (file) => file.category === "frontend"
            ),
        [development.attachments]
    );

    const backendFiles = useMemo(
        () =>
            (development.attachments ?? []).filter(
                (file) => file.category === "backend"
            ),
        [development.attachments]
    );

    const designLinks = useMemo(
        () =>
            parseTechnicalLinks(development).filter(
                (link) =>
                    link.name.toLowerCase().includes("figma") ||
                    link.name.toLowerCase().includes("design") ||
                    link.name.toLowerCase().includes("diseño")
            ),
        [development.metadata]
    );

    const generateDocument = async (): Promise<void> => {
        setGeneratingDocument(true);
        setDocumentError(null);

        try {
            const result = await developmentApi.generateRequirements(
                development.id
            );
            if (!result.result) {
                throw new Error(
                    result.message ||
                        t("developments.detail.requirements_documents_error")
                );
            }

            const detail = await developmentApi.get(development.id);
            if (detail.result) {
                onUpdate(detail.data);
            }
            setDocumentsOpen(true);
            showSuccess(
                t("developments.detail.requirements_documents_success")
            );
        } catch (error) {
            setDocumentError(
                error instanceof Error
                    ? error.message
                    : t("developments.detail.requirements_documents_error")
            );
        } finally {
            setGeneratingDocument(false);
        }
    };
    const [category, setCategory] = useState<string>("documentation");
    const [search, setSearch] = useState<string>("");
    const [designSearch, setDesignSearch] = useState<string>("");
    const [linksSearch, setLinksSearch] = useState<string>("");
    const [notesSearch, setNotesSearch] = useState<string>("");
    const [openCategories, setOpenCategories] = useState<
        Record<string, boolean>
    >({});
    const [attachmentToDelete, setAttachmentToDelete] =
        useState<Attachment | null>(null);
    const [attachmentToRename, setAttachmentToRename] =
        useState<Attachment | null>(null);
    const [attachmentToMakePublic, setAttachmentToMakePublic] =
        useState<Attachment | null>(null);
    const [attachmentToMakeInternal, setAttachmentToMakeInternal] =
        useState<Attachment | null>(null);
    const [renameFormName, setRenameFormName] = useState<string>("");
    const [isRenamingAttachment, setIsRenamingAttachment] =
        useState<boolean>(false);
    const [isDeletingAttachment, setIsDeletingAttachment] =
        useState<boolean>(false);
    const [isMakingPublic, setIsMakingPublic] = useState<boolean>(false);
    const [isMakingInternal, setIsMakingInternal] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
    const [editingLinkId, setEditingLinkId] = useState<string | null>(null);
    const [linkFormName, setLinkFormName] = useState("");
    const [linkFormUrl, setLinkFormUrl] = useState("");
    const [linkFormIsInternal, setLinkFormIsInternal] = useState(false);
    const [linkToDelete, setLinkToDelete] = useState<TechnicalLink | null>(
        null
    );
    const [isSavingLink, setIsSavingLink] = useState(false);
    const [isDeletingLink, setIsDeletingLink] = useState(false);
    const [isSuggestTasksModalOpen, setIsSuggestTasksModalOpen] =
        useState(false);
    const [isNewTaskModalOpen, setIsNewTaskModalOpen] = useState(false);
    const [taskStatuses, setTaskStatuses] = useState<any[]>([]);
    const [statusTarget, setStatusTarget] = useState<any | null>(null);
    const [assigneeTarget, setAssigneeTarget] = useState<any | null>(null);
    const [deleteAssociatedReqDoc, setDeleteAssociatedReqDoc] =
        useState<boolean>(true);
    const [deleteAssociatedDesigns, setDeleteAssociatedDesigns] =
        useState<boolean>(true);
    const [deleteAssociatedPairedDesign, setDeleteAssociatedPairedDesign] =
        useState<boolean>(true);
    const [deleteAssociatedReqForDesign, setDeleteAssociatedReqForDesign] =
        useState<boolean>(false);

    const handleOpenNewTask = async () => {
        try {
            const res = await taskStatusesApi.list();
            if (res.result) {
                setTaskStatuses(res.data);
                setIsNewTaskModalOpen(true);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const associatedReqDoc = useMemo<Attachment | null>(() => {
        if (
            !attachmentToDelete ||
            attachmentToDelete.category !== "requirements_document"
        ) {
            return null;
        }
        const isPdf = attachmentToDelete.name.toLowerCase().endsWith(".pdf");
        const isMd = attachmentToDelete.name.toLowerCase().endsWith(".md");
        if (!isPdf && !isMd) {
            return null;
        }
        const baseName = isPdf
            ? attachmentToDelete.name.slice(0, -4)
            : attachmentToDelete.name.slice(0, -3);

        const targetExt = isPdf ? ".md" : ".pdf";
        const targetName = baseName + targetExt;

        return (
            (development.attachments ?? []).find(
                (a: Attachment) =>
                    a.category === "requirements_document" &&
                    a.name.toLowerCase() === targetName.toLowerCase() &&
                    a.id !== attachmentToDelete.id
            ) || null
        );
    }, [attachmentToDelete, development.attachments]);

    const associatedDesignsForReq = useMemo<Attachment[]>(() => {
        if (
            !attachmentToDelete ||
            attachmentToDelete.category !== "requirements_document"
        ) {
            return [];
        }

        const reqIds = new Set<number>([attachmentToDelete.id]);
        const reqNames = new Set<string>([
            attachmentToDelete.name.toLowerCase(),
        ]);

        if (associatedReqDoc) {
            reqIds.add(associatedReqDoc.id);
            reqNames.add(associatedReqDoc.name.toLowerCase());
        }

        const baseName1 = attachmentToDelete.name
            .replace(/\.(pdf|md|markdown)$/i, "")
            .toLowerCase();
        const baseName2 = associatedReqDoc
            ? associatedReqDoc.name
                  .replace(/\.(pdf|md|markdown)$/i, "")
                  .toLowerCase()
            : "";

        return (development.attachments ?? []).filter((a: Attachment) => {
            if (a.category !== "design" || a.id === attachmentToDelete.id) {
                return false;
            }

            if (
                a.requirement_attachment_id &&
                reqIds.has(a.requirement_attachment_id)
            ) {
                return true;
            }

            if (a.requirement_attachment_name) {
                const norm = a.requirement_attachment_name.toLowerCase();
                const stem = norm.replace(/\.(pdf|md|markdown)$/i, "");
                if (
                    reqNames.has(norm) ||
                    stem === baseName1 ||
                    (baseName2 && stem === baseName2)
                ) {
                    return true;
                }
            }

            return false;
        });
    }, [attachmentToDelete, associatedReqDoc, development.attachments]);

    const associatedPairedDesign = useMemo<Attachment | null>(() => {
        if (!attachmentToDelete || attachmentToDelete.category !== "design") {
            return null;
        }

        const isPdf = attachmentToDelete.name.toLowerCase().endsWith(".pdf");
        const isHtml =
            attachmentToDelete.name.toLowerCase().endsWith(".html") ||
            attachmentToDelete.name.toLowerCase().endsWith(".htm");
        if (!isPdf && !isHtml) {
            return null;
        }

        const baseName = isPdf
            ? attachmentToDelete.name.slice(0, -4)
            : attachmentToDelete.name.replace(/\.html?$/i, "");

        const targetExt = isPdf ? ".html" : ".pdf";

        return (
            (development.attachments ?? []).find((a: Attachment) => {
                if (a.category !== "design" || a.id === attachmentToDelete.id) {
                    return false;
                }

                const otherBase = a.name.replace(/\.(pdf|html?)$/i, "");
                return (
                    otherBase.toLowerCase() === baseName.toLowerCase() &&
                    a.name.toLowerCase().endsWith(targetExt)
                );
            }) ?? null
        );
    }, [attachmentToDelete, development.attachments]);

    const associatedReqsForDesign = useMemo<Attachment[]>(() => {
        if (!attachmentToDelete || attachmentToDelete.category !== "design") {
            return [];
        }

        const allReqs = (development.attachments ?? []).filter(
            (a: Attachment) => a.category === "requirements_document"
        );
        if (allReqs.length === 0) {
            return [];
        }

        let primaryReq: Attachment | null = null;
        if (attachmentToDelete.requirement_attachment_id) {
            primaryReq =
                allReqs.find(
                    (a: Attachment) =>
                        a.id === attachmentToDelete.requirement_attachment_id
                ) ?? null;
        }

        if (!primaryReq && attachmentToDelete.requirement_attachment_name) {
            const nameNorm =
                attachmentToDelete.requirement_attachment_name.toLowerCase();
            const stemNorm = nameNorm.replace(/\.(pdf|md|markdown)$/i, "");
            primaryReq =
                allReqs.find((a: Attachment) => {
                    const docName = a.name.toLowerCase();
                    const docStem = docName.replace(
                        /\.(pdf|md|markdown)$/i,
                        ""
                    );
                    return docName === nameNorm || docStem === stemNorm;
                }) ?? null;
        }

        if (!primaryReq) {
            return [];
        }

        const primaryStem = primaryReq.name
            .replace(/\.(pdf|md|markdown)$/i, "")
            .toLowerCase();
        return allReqs.filter((a: Attachment) => {
            if (a.id === primaryReq?.id) {
                return true;
            }
            const aStem = a.name
                .replace(/\.(pdf|md|markdown)$/i, "")
                .toLowerCase();
            return aStem === primaryStem;
        });
    }, [attachmentToDelete, development.attachments]);

    const deleteModalTitle = useMemo<string>(() => {
        if (!attachmentToDelete) {
            return "";
        }
        if (attachmentToDelete.category === "requirements_document") {
            return t("developments.detail.delete_requirements_document_title");
        }
        if (attachmentToDelete.category === "design") {
            return t("developments.detail.delete_design_title");
        }
        return t("developments.detail.delete_attachment_title");
    }, [attachmentToDelete, t]);

    const deleteModalDesc = useMemo<string>(() => {
        if (!attachmentToDelete) {
            return "";
        }
        if (attachmentToDelete.category === "requirements_document") {
            return t("developments.detail.delete_requirements_document_desc");
        }
        if (attachmentToDelete.category === "design") {
            return t("developments.detail.delete_design_desc");
        }
        return t("developments.detail.delete_attachment_desc");
    }, [attachmentToDelete, t]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const dragCounter = useRef<number>(0);
    const [isDragOver, setIsDragOver] = useState<boolean>(false);

    const canManageLink = (link: TechnicalLink): boolean => {
        if (
            link.id === "demo-env" ||
            link.id === "prod-env" ||
            link.id === "legacy"
        )
            return false;

        if (!isEditor) {
            return false;
        }

        if (isIt && !isClient) {
            return true;
        }

        return Boolean(
            user?.id && link.user_id && Number(link.user_id) === Number(user.id)
        );
    };

    const canManageAttachment = (attachment: Attachment): boolean => {
        if (isIt && !isClient) {
            return true;
        }

        if (!isEditor) {
            return false;
        }

        const ownerId = attachment.user_id ?? attachment.user?.id;
        return Boolean(
            user?.id && ownerId && Number(ownerId) === Number(user.id)
        );
    };

    const canDeleteAttachment = (attachment: Attachment): boolean => {
        if (attachment.category === "requirements_document") {
            const reqDocs = (development.attachments ?? []).filter(
                (a) => a.category === "requirements_document"
            );
            if (reqDocs.length <= 1) {
                return false;
            }
        }

        if (attachment.category === "design") {
            // Se permite eliminar diseños aunque sea el último, porque siempre pueden generar otro.
        }
        if ((isIt || isDevelopment) && !isClient) {
            return true;
        }

        if (!isEditor) {
            return false;
        }

        if (
            attachment.category === "requirements_document" ||
            attachment.category === "design"
        ) {
            return false;
        }

        return canManageAttachment(attachment);
    };

    const attachments = useMemo(() => {
        const list = development.attachments ?? [];
        if (isClient) {
            return list.filter((att) => !att.is_internal);
        }

        return list;
    }, [development.attachments, isClient]);

    const attachmentMessageInfo = useMemo(() => {
        const map = new Map<number, { messageId: number; createdAt: string }>();

        attachments.forEach((attachment: Attachment) => {
            const msgId =
                attachment.message_id ??
                (attachment as unknown as { development_message_id?: number })
                    .development_message_id;
            if (msgId) {
                map.set(attachment.id, {
                    messageId: msgId,
                    createdAt: attachment.created_at ?? "",
                });
            }
        });

        (development.messages ?? []).forEach((message) => {
            if (isClient && message.is_internal) {
                return;
            }

            message.attachments?.forEach((attachment) => {
                map.set(attachment.id, {
                    messageId: message.id,
                    createdAt: message.created_at,
                });
            });

            const msgType =
                message.type ||
                (message as { type_model?: { tag?: string } }).type_model?.tag;
            if (msgType === "ai_response") {
                const reqId = (
                    message.metadata as { requirement_attachment_id?: number }
                )?.requirement_attachment_id;
                attachments.forEach((att: Attachment) => {
                    if (att.category === "design" && !map.has(att.id)) {
                        if (reqId && att.requirement_attachment_id === reqId) {
                            map.set(att.id, {
                                messageId: message.id,
                                createdAt: message.created_at,
                            });
                        }
                    }
                });
            }
        });

        return map;
    }, [attachments, development.messages, isClient]);
    const allTechnicalLinks = useMemo(() => {
        const parsed = parseTechnicalLinks(development);

        const isDeploymentCompleted =
            development.steps?.some(
                (s) =>
                    (s.tag === "deployment" || s.step_key === "deployment") &&
                    s.is_completed
            ) ?? false;

        if (isDeploymentCompleted) {
            const prodUrl = import.meta.env.VITE_FRONTEND_PROD;
            if (prodUrl) {
                const absoluteProdUrl = prodUrl.startsWith("http")
                    ? prodUrl
                    : `https://${prodUrl}`;
                parsed.unshift({
                    id: "prod-env",
                    name: "Entorno de Producción",
                    url: absoluteProdUrl,
                    is_internal: false,
                });
            }
        } else {
            const demoUrl = import.meta.env.VITE_FRONTEND_DEMO;
            if (demoUrl) {
                const absoluteDemoUrl = demoUrl.startsWith("http")
                    ? demoUrl
                    : `https://${demoUrl}`;
                parsed.unshift({
                    id: "demo-env",
                    name: "Servidor de Validación (Demo)",
                    url: absoluteDemoUrl,
                    is_internal: false,
                });
            }
        }

        return parsed;
    }, [development]);
    const technicalLinks = useMemo(() => {
        if (isClient) {
            return allTechnicalLinks.filter((link) => !link.is_internal);
        }

        return allTechnicalLinks;
    }, [allTechnicalLinks, isClient]);
    const tickets = parseTicketsList(
        null,
        development.tickets,
        development.metadata?.tickets ??
            development.metadata?.jira_tasks ??
            development.metadata?.tasks
    );
    const internalNotes = useMemo(() => {
        if (isClient) {
            return [];
        }

        return (development.messages ?? []).filter((message) => {
            const isSystem =
                message.type === "system" || message.type === "step_event";
            const isAi = message.type === "ai_response";
            return Boolean(message.is_internal) && !isSystem && !isAi;
        });
    }, [development.messages, isClient]);
    const [openSections, setOpenSections] = useState<
        Record<PanelSectionKey, boolean>
    >({
        attachments: true,
        design: true,
        technicalLinks: true,
        internalNotes: true,
        internalTasks: true,
    });
    const toggleSection = (key: PanelSectionKey): void => {
        setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
    };
    const nonDesignAttachments = useMemo(() => {
        return attachments.filter((attachment) => {
            if (isClient && attachment.is_internal) {
                return false;
            }

            const category = attachment.category?.toLowerCase() ?? "";
            return (
                category !== "design" && category !== "requirements_document"
            );
        });
    }, [attachments, isClient]);

    const designAttachments = useMemo(() => {
        return attachments.filter((attachment) => {
            if (isClient && attachment.is_internal) {
                return false;
            }

            return attachment.category?.toLowerCase() === "design";
        });
    }, [attachments, isClient]);

    const aiAttachments = useMemo(() => {
        if (isClient) {
            return [];
        }

        return nonDesignAttachments.filter((att) => {
            const messageInfo = attachmentMessageInfo.get(att.id);
            return (
                att.category?.toLowerCase() === "ai" ||
                (messageInfo && messageInfo.message?.type === "ai_response")
            );
        });
    }, [nonDesignAttachments, attachmentMessageInfo, isClient]);

    const uploadedAttachments = useMemo(() => {
        return nonDesignAttachments.filter((att) => {
            const messageInfo = attachmentMessageInfo.get(att.id);
            const isAi =
                att.category?.toLowerCase() === "ai" ||
                (messageInfo && messageInfo.message?.type === "ai_response");
            return (
                att.category?.toLowerCase() !== "chat" && !messageInfo && !isAi
            );
        });
    }, [nonDesignAttachments, attachmentMessageInfo]);

    const chatAttachments = useMemo(() => {
        return nonDesignAttachments.filter((att) => {
            const messageInfo = attachmentMessageInfo.get(att.id);
            const isAi =
                att.category?.toLowerCase() === "ai" ||
                (messageInfo && messageInfo.message?.type === "ai_response");
            return (
                !isAi &&
                (att.category?.toLowerCase() === "chat" || Boolean(messageInfo))
            );
        });
    }, [nonDesignAttachments, attachmentMessageInfo]);

    const isInTasksPhaseOrHigher = useMemo(() => {
        const steps = development.steps ?? [];
        if (steps.length === 0) return false;

        const tasksIdx = steps.findIndex(
            (s) => (s.tag || s.step_key) === "tasks"
        );
        if (tasksIdx === -1) return false;

        const currentIdx = steps.findIndex((s) => !s.is_completed);

        if (currentIdx === -1) return true;

        return currentIdx >= tasksIdx;
    }, [development.steps]);

    const filteredAttachments = useMemo(() => {
        const needle = search.trim();
        let listToFilter: Attachment[] = [];

        switch (activeFileTab) {
            case "uploaded":
                listToFilter = uploadedAttachments;
                break;
            case "chat":
                listToFilter = chatAttachments;
                break;
            case "ai":
                listToFilter = aiAttachments;
                break;
        }

        if (!needle) {
            return listToFilter;
        }

        return listToFilter.filter((attachment) =>
            matchesPanelSearch(
                needle,
                attachment.name,
                attachment.category ?? ""
            )
        );
    }, [
        uploadedAttachments,
        chatAttachments,
        aiAttachments,
        activeFileTab,
        search,
    ]);

    const filteredDesignAttachments = useMemo(() => {
        const needle = designSearch.trim();
        if (!needle) {
            return designAttachments;
        }
        return designAttachments.filter((attachment) =>
            matchesPanelSearch(needle, attachment.name)
        );
    }, [designAttachments, designSearch]);

    const filteredTechnicalLinks = useMemo(() => {
        const needle = linksSearch.trim();
        if (!needle) {
            return technicalLinks;
        }
        return technicalLinks.filter((link) =>
            matchesPanelSearch(needle, link.name, link.url)
        );
    }, [technicalLinks, linksSearch]);

    const filteredInternalNotes = useMemo(() => {
        const needle = notesSearch.trim();
        if (!needle) {
            return internalNotes;
        }
        return internalNotes.filter((note) =>
            matchesPanelSearch(
                needle,
                getInternalNotePreview(note.content),
                date(note.created_at, true, true)
            )
        );
    }, [internalNotes, notesSearch]);

    const groupedAttachments = useMemo(() => {
        const groups: Record<string, Attachment[]> = {};

        FILE_CATEGORIES.forEach((cat) => {
            groups[cat] = [];
        });

        filteredAttachments.forEach((att) => {
            const normalized = att.category?.toLowerCase() ?? "";
            if (normalized === "design") {
                return;
            }
            const cat =
                normalized && groups[normalized] !== undefined
                    ? normalized
                    : "other";
            if (!groups[cat]) {
                groups[cat] = [];
            }
            groups[cat].push(att);
        });

        return Object.entries(groups).filter(([_, items]) => items.length > 0);
    }, [filteredAttachments]);

    const toggleCategory = (catName: string): void => {
        setOpenCategories((prev) => ({
            ...prev,
            [catName]: !(prev[catName] ?? true),
        }));
    };

    const resetDrag = (): void => {
        dragCounter.current = 0;
        setIsDragOver(false);
    };

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === "Escape") {
                resetDrag();
            }
        };
        const onDragEnd = (): void => {
            resetDrag();
        };
        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("dragend", onDragEnd);
        return () => {
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("dragend", onDragEnd);
        };
    }, []);

    const handleDragEnter = (event: React.DragEvent<HTMLElement>): void => {
        if (!isEditor || uploading) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        dragCounter.current += 1;
        if (event.dataTransfer.items?.length) {
            setIsDragOver(true);
        }
    };

    const handleDragLeave = (event: React.DragEvent<HTMLElement>): void => {
        if (!isEditor) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        dragCounter.current -= 1;
        if (dragCounter.current <= 0) {
            resetDrag();
        }
    };

    const handleDragOver = (event: React.DragEvent<HTMLElement>): void => {
        if (!isEditor || uploading) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
    };

    const uploadFiles = async (files: File[]): Promise<void> => {
        if (files.length === 0 || !isEditor) {
            return;
        }

        setUploading(true);
        setError(null);

        try {
            for (const file of files) {
                const defaultReqId =
                    category === "design"
                        ? ((development.attachments ?? [])
                              .filter(
                                  (a: Attachment) =>
                                      a.category === "requirements_document"
                              )
                              .slice(-1)[0]?.id ?? null)
                        : null;
                await developmentApi.uploadAttachment(
                    development.id,
                    file,
                    null,
                    isIt && !isClient && isInternalFile,
                    category,
                    defaultReqId
                );
            }
            await refresh();
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : t("developments.detail.unexpected_error")
            );
        } finally {
            setUploading(false);
        }
    };

    const handleDrop = async (
        event: React.DragEvent<HTMLElement>
    ): Promise<void> => {
        if (!isEditor || uploading) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        resetDrag();

        const files = Array.from(event.dataTransfer.files ?? []);
        await uploadFiles(files);
    };

    const refresh = async (): Promise<void> => {
        const response = await developmentApi.get(development.id);
        if (response.result) {
            onUpdate(response.data);
        }
    };

    const persistTechnicalLinks = async (
        links: TechnicalLink[]
    ): Promise<void> => {
        const nextMetadata = { ...(development.metadata ?? {}) };
        nextMetadata.technical_links = links.filter(
            (link) =>
                link.id !== "demo-env" &&
                link.id !== "prod-env" &&
                link.id !== "legacy"
        );
        delete nextMetadata.design_link_name;

        const response = await developmentApi.update(development.id, {
            design_url: null,
            figma_url: null,
            metadata: nextMetadata,
        });
        if (!response.result) {
            throw new Error(response.message);
        }
    };

    const openAddTechnicalLink = (): void => {
        setEditingLinkId(null);
        setLinkFormName("");
        setLinkFormUrl("");
        setLinkFormIsInternal(false);
        setIsLinkModalOpen(true);
    };

    const openEditTechnicalLink = (link: TechnicalLink): void => {
        if (!canManageLink(link)) {
            return;
        }

        setEditingLinkId(link.id);
        setLinkFormName(link.name);
        setLinkFormUrl(link.url);
        setLinkFormIsInternal(Boolean(link.is_internal));
        setIsLinkModalOpen(true);
    };

    const saveTechnicalLink = async (): Promise<void> => {
        const trimmedName = linkFormName.trim();
        const trimmedUrl = linkFormUrl.trim();

        if (!trimmedName) {
            setError(t("developments.detail.technical_link_name_required"));
            return;
        }

        if (!trimmedUrl) {
            setError(t("developments.detail.technical_link_url_required"));
            return;
        }

        setIsSavingLink(true);
        setError(null);

        try {
            const nextLinks = [...allTechnicalLinks];
            if (editingLinkId) {
                const index = nextLinks.findIndex(
                    (link) => link.id === editingLinkId
                );
                if (index >= 0) {
                    if (!canManageLink(nextLinks[index])) {
                        setError(t("developments.detail.unexpected_error"));
                        return;
                    }

                    nextLinks[index] = {
                        ...nextLinks[index],
                        name: trimmedName,
                        url: trimmedUrl,
                        is_internal:
                            isIt && !isClient
                                ? linkFormIsInternal
                                : nextLinks[index].is_internal,
                    };
                }
            } else {
                nextLinks.push({
                    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                    name: trimmedName,
                    url: trimmedUrl,
                    user_id: user?.id,
                    is_internal: isIt && !isClient ? linkFormIsInternal : false,
                });
            }

            await persistTechnicalLinks(nextLinks);
            setIsLinkModalOpen(false);
            setEditingLinkId(null);
            setLinkFormName("");
            setLinkFormUrl("");
            setLinkFormIsInternal(false);
            await refresh();
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : t("developments.detail.unexpected_error")
            );
        } finally {
            setIsSavingLink(false);
        }
    };

    const deleteTechnicalLink = async (): Promise<void> => {
        if (!linkToDelete || !canManageLink(linkToDelete)) {
            return;
        }

        setIsDeletingLink(true);
        setError(null);

        try {
            const nextLinks = allTechnicalLinks.filter(
                (link) => link.id !== linkToDelete.id
            );
            await persistTechnicalLinks(nextLinks);
            setLinkToDelete(null);
            await refresh();
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : t("developments.detail.unexpected_error")
            );
        } finally {
            setIsDeletingLink(false);
        }
    };

    const openRenameAttachment = (attachment: Attachment): void => {
        if (!canManageAttachment(attachment)) {
            return;
        }

        setAttachmentToRename(attachment);
        setRenameFormName(attachment.name);
    };

    const handleRenameAttachment = async (): Promise<void> => {
        if (!attachmentToRename || !canManageAttachment(attachmentToRename)) {
            return;
        }

        const trimmedName = renameFormName.trim();
        if (!trimmedName) {
            setError(t("developments.detail.attachment_name_required"));
            return;
        }

        if (trimmedName === attachmentToRename.name) {
            setAttachmentToRename(null);
            setRenameFormName("");
            return;
        }

        setIsRenamingAttachment(true);
        setError(null);

        try {
            const res = await developmentApi.renameAttachment(
                development.id,
                attachmentToRename.id,
                trimmedName
            );
            if (res.result) {
                setAttachmentToRename(null);
                setRenameFormName("");
                if (fileViewerAttachment?.id === attachmentToRename.id) {
                    setFileViewerAttachment(null);
                }
                await refresh();
            } else {
                setError(
                    res.message || t("developments.detail.unexpected_error")
                );
            }
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : t("developments.detail.unexpected_error")
            );
        } finally {
            setIsRenamingAttachment(false);
        }
    };

    const handleConfirmDeleteAttachment = async (): Promise<void> => {
        if (!attachmentToDelete || !canDeleteAttachment(attachmentToDelete)) {
            return;
        }

        setIsDeletingAttachment(true);
        setError(null);

        try {
            const idsToDelete: number[] = [attachmentToDelete.id];

            if (attachmentToDelete.category === "requirements_document") {
                if (deleteAssociatedReqDoc && associatedReqDoc) {
                    idsToDelete.push(associatedReqDoc.id);
                }
                if (
                    deleteAssociatedDesigns &&
                    associatedDesignsForReq.length > 0
                ) {
                    associatedDesignsForReq.forEach((d: Attachment) => {
                        if (!idsToDelete.includes(d.id)) {
                            idsToDelete.push(d.id);
                        }
                    });
                }
            } else {
                if (attachmentToDelete.category === "design") {
                    if (
                        deleteAssociatedPairedDesign &&
                        associatedPairedDesign
                    ) {
                        idsToDelete.push(associatedPairedDesign.id);
                    }
                    if (
                        deleteAssociatedReqForDesign &&
                        associatedReqsForDesign.length > 0
                    ) {
                        associatedReqsForDesign.forEach((r: Attachment) => {
                            if (!idsToDelete.includes(r.id)) {
                                idsToDelete.push(r.id);
                            }
                        });
                    }
                }
            }

            for (const id of idsToDelete) {
                await developmentApi.deleteAttachment(development.id, id);
            }

            setAttachmentToDelete(null);
            setIsLightboxOpen(false);
            setFileViewerAttachment(null);
            await refresh();
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : t("developments.detail.unexpected_error")
            );
        } finally {
            setIsDeletingAttachment(false);
        }
    };

    const deleteAttachmentDirect = async (
        attachmentId: number
    ): Promise<void> => {
        const att = attachments.find((a) => a.id === attachmentId);
        if (!att || !canDeleteAttachment(att)) {
            return;
        }

        setIsDeletingAttachment(true);
        setError(null);

        try {
            const res = await developmentApi.deleteAttachment(
                development.id,
                attachmentId
            );
            if (res.result) {
                setIsLightboxOpen(false);
                setFileViewerAttachment(null);
                await refresh();
                return;
            }

            throw new Error(
                res.message || t("developments.detail.unexpected_error")
            );
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : t("developments.detail.unexpected_error")
            );
        } finally {
            setIsDeletingAttachment(false);
        }
    };

    const handleConfirmMakePublic = async (): Promise<void> => {
        if (!attachmentToMakePublic || !isIt || isClient) {
            return;
        }

        setIsMakingPublic(true);
        setError(null);

        try {
            const res = await developmentApi.updateAttachment(
                development.id,
                attachmentToMakePublic.id,
                {
                    is_internal: false,
                }
            );

            if (res.result) {
                setAttachmentToMakePublic(null);
                await refresh();
            } else {
                setError(
                    res.message || t("developments.detail.unexpected_error")
                );
            }
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : t("developments.detail.unexpected_error")
            );
        } finally {
            setIsMakingPublic(false);
        }
    };

    const handleConfirmMakeInternal = async (): Promise<void> => {
        if (!attachmentToMakeInternal || !isIt || isClient) {
            return;
        }

        setIsMakingInternal(true);
        setError(null);

        const targetId = attachmentToMakeInternal.id;

        try {
            const res = await developmentApi.updateAttachment(
                development.id,
                targetId,
                {
                    is_internal: true,
                }
            );

            if (res.result) {
                setAttachmentToMakeInternal(null);
                await refresh();
            } else {
                setError(
                    res.message || t("developments.detail.unexpected_error")
                );
            }
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : t("developments.detail.unexpected_error")
            );
        } finally {
            setIsMakingInternal(false);
        }
    };

    const handleDeleteTicket = async (ticketKey: string): Promise<void> => {
        const nextMetadata = development.metadata
            ? { ...development.metadata }
            : undefined;
        if (nextMetadata) {
            if (Array.isArray(nextMetadata.tickets)) {
                nextMetadata.tickets = nextMetadata.tickets.filter(
                    (t: TicketItem) => t.key !== ticketKey
                );
            }
            if (Array.isArray(nextMetadata.jira_tasks)) {
                nextMetadata.jira_tasks = nextMetadata.jira_tasks.filter(
                    (t: TicketItem) => t.key !== ticketKey
                );
            }
            if (Array.isArray(nextMetadata.tasks)) {
                nextMetadata.tasks = nextMetadata.tasks.filter(
                    (t: TicketItem) => t.key !== ticketKey
                );
            }
        }

        const res = await developmentApi.update(development.id, {
            metadata: nextMetadata,
        });

        if (res.result) {
            await refresh();
        } else {
            throw new Error(
                res.message || t("developments.detail.ticket_delete_error")
            );
        }
    };

    const handleDownloadAttachment = async (
        attachment: Attachment
    ): Promise<void> => {
        try {
            showSuccess(t("developments.detail.downloading_file"));
            const blob = await developmentApi.getAttachmentBlob(
                development.id,
                attachment.id
            );
            saveAs(blob, attachment.name);
        } catch (error: unknown) {
            console.error(error);
            showError(t("developments.detail.download_error"));
        }
    };

    const handleDownloadReactCode = async (attachment: Attachment) => {
        try {
            showSuccess("Descargando código React...");
            const blobHtml = await developmentApi.getAttachmentBlob(
                development.id,
                attachment.id
            );
            const htmlText = await blobHtml.text();

            const match = htmlText.match(
                /<script type="text\/plain" id="tsx-source">([\s\S]*?)<\/script>/
            );
            let tsxContent = "";

            if (match && match[1]) {
                try {
                    const bytes = Uint8Array.from(atob(match[1]), (c) =>
                        c.charCodeAt(0)
                    );
                    tsxContent = new TextDecoder().decode(bytes);
                } catch (e) {
                    tsxContent = match[1];
                }
            } else {
                showError(
                    "Este diseño no contiene el código original o ha expirado."
                );
                return;
            }

            const blob = new Blob([tsxContent], { type: "text/plain" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = attachment.name.replace(/\.[^/.]+$/, "") + ".tsx";
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error(error);
            showError("Error al descargar el código React");
        }
    };

    const handleDownloadTask = (task: any) => {
        const content = `# ${task.title}\n\n${task.description || ""}`;
        const blob = new Blob([content], { type: "text/markdown" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `tarea_${task.code}.md`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        showSuccess(t("developments.detail.task_downloaded"));
    };

    const handleCopyTask = (task: any) => {
        const content = `# ${task.title}\n\n${task.description || ""}`;
        navigator.clipboard.writeText(content).then(() => {
            showSuccess(t("developments.detail.task_copied"));
        });
    };

    const handleUpload = async (
        event: React.ChangeEvent<HTMLInputElement>
    ): Promise<void> => {
        const files = Array.from(event.target.files ?? []);
        await uploadFiles(files);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const imageAttachments = useMemo(() => {
        return attachments.filter((att) =>
            isImageFile(att.name, att.mime_type)
        );
    }, [attachments]);

    const lightboxItems = useMemo<LightboxItem[]>(() => {
        return imageAttachments.map((att) => ({
            id: att.id,
            title: att.name,
            loadBlob: () =>
                developmentApi.getAttachmentBlob(development.id, att.id),
            canDelete: canDeleteAttachment(att),
        }));
    }, [imageAttachments, development.id, isIt, isClient, user?.id, isEditor]);

    const [lightboxIndex, setLightboxIndex] = useState<number>(0);
    const [lightboxId, setLightboxId] = useState<number | string | undefined>(
        undefined
    );
    const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>(false);
    const [fileViewerAttachment, setFileViewerAttachment] =
        useState<Attachment | null>(null);
    const [reqDocViewerAttachment, setReqDocViewerAttachment] =
        useState<Attachment | null>(null);

    const handleAttachmentClick = (attachment: Attachment): void => {
        if (attachment.category === "design" && attachment.file_path) {
            window.open(attachment.file_path, "_blank");
            return;
        }

        if (!isImageFile(attachment.name, attachment.mime_type)) {
            setFileViewerAttachment(attachment);
            return;
        }

        const idx = imageAttachments.findIndex(
            (att) => String(att.id) === String(attachment.id)
        );
        setLightboxIndex(idx >= 0 ? idx : 0);
        setLightboxId(attachment.id);
        setIsLightboxOpen(true);
    };

    const selectedCategoryLabel =
        fileCategoryOptions.find((option) => option.value === category)
            ?.label ?? category;

    const getAttachmentVersion = (attachment: Attachment): number | null => {
        const match = attachment.name.match(
            /(?:[-_\s]|^)v(\d+)(?=[^a-z0-9]|$)/i
        );
        if (match) {
            return parseInt(match[1], 10);
        }

        const cat = attachment.category?.toLowerCase();
        if (cat === "requirements_document") {
            const isMd =
                attachment.name.toLowerCase().endsWith(".md") ||
                Boolean(attachment.is_internal);
            const list = isMd ? internalRequirementDocs : clientRequirementDocs;
            const sorted = [...list].sort((a, b) => {
                const timeA = a.created_at
                    ? new Date(a.created_at).getTime()
                    : a.id;
                const timeB = b.created_at
                    ? new Date(b.created_at).getTime()
                    : b.id;
                return timeA - timeB;
            });
            const index = sorted.findIndex((item) => item.id === attachment.id);
            if (index !== -1) {
                return index + 1;
            }
        } else {
            if (cat === "design") {
                const sorted = [...designFiles].sort((a, b) => {
                    const timeA = a.created_at
                        ? new Date(a.created_at).getTime()
                        : a.id;
                    const timeB = b.created_at
                        ? new Date(b.created_at).getTime()
                        : b.id;
                    return timeA - timeB;
                });
                const index = sorted.findIndex(
                    (item) => item.id === attachment.id
                );
                if (index !== -1) {
                    return index + 1;
                }
            }
        }

        return null;
    };

    const renderAttachmentRow = (attachment: Attachment): React.ReactNode => {
        const fileIcon = getAttachmentFileIcon(
            attachment.name,
            attachment.mime_type
        );
        const messageInfo = attachmentMessageInfo.get(attachment.id);
        const isChatFile =
            attachment.category?.toLowerCase() === "chat" ||
            Boolean(messageInfo);
        const isRequirement =
            attachment.category?.toLowerCase() === "requirements_document";
        const isDesign = attachment.category?.toLowerCase() === "design";
        const isRequirementOrDesign = isRequirement || isDesign;
        const versionNumber = isRequirementOrDesign
            ? getAttachmentVersion(attachment)
            : null;
        const attachmentDate = attachment.created_at || messageInfo?.createdAt;
        const dateLabel = attachmentDate
            ? date(attachmentDate, true, true)
            : "";
        const sizeLabel = formatFileSize(attachment.file_size);
        const sourceReqDocName =
            attachment.requirement_attachment_name ||
            (attachment.category === "design" &&
            attachment.requirement_attachment_id
                ? (development.attachments ?? []).find(
                      (a: Attachment) =>
                          a.id === attachment.requirement_attachment_id
                  )?.name
                : null);

        const hasGenerateDesign = Boolean(
            attachment.category === "requirements_document" &&
                isEditor &&
                isIt &&
                !isClient &&
                onOpenDesignModal &&
                isInDesignPhase
        );
        const hasDownloadCode = Boolean(
            attachment.category === "design" &&
                isEditor &&
                isIt &&
                !isClient /*&& isInFrontendPhase*/
        );
        const hasGoToRequirements = Boolean(
            sourceReqDocName && attachment.requirement_attachment_id
        );
        const hasViewInChat = Boolean(
            (isChatFile || attachment.category === "design") &&
                messageInfo &&
                onNavigateToMessage
        );
        const hasMakePublic = Boolean(
            attachment.is_internal &&
                isIt &&
                !isClient &&
                attachment.category !== "requirements_document"
        );
        const hasMakeInternal = Boolean(
            !attachment.is_internal &&
                attachment.category === "design" &&
                isIt &&
                !isClient
        );
        const hasEdit = Boolean(
            attachment.category !== "requirements_document" &&
                canManageAttachment(attachment)
        );
        const hasDelete = Boolean(canDeleteAttachment(attachment));
        const hasActions = true;

        return (
            <div
                key={attachment.id}
                id={`attachment-${attachment.id}`}
                draggable={isEditor}
                onDragStart={(event) => {
                    event.dataTransfer.setData(
                        ATTACHMENT_DRAG_MIME,
                        JSON.stringify({
                            id: attachment.id,
                            name: attachment.name,
                        })
                    );
                    event.dataTransfer.effectAllowed = "copy";

                    const dragGhost = document.createElement("div");
                    dragGhost.textContent = attachment.name;
                    dragGhost.className =
                        "px-3 py-1.5 bg-primary text-white rounded-lg shadow-md text-xs font-semibold absolute -top-full -left-full z-[-1]";
                    document.body.appendChild(dragGhost);
                    event.dataTransfer.setDragImage(dragGhost, 0, 0);
                    setTimeout(() => document.body.removeChild(dragGhost), 0);
                }}
                className={`group flex flex-col w-full min-w-0 rounded-xl border overflow-hidden text-left text-xs transition hover:border-primary/60 ${
                    isEditor ? "cursor-grab active:cursor-grabbing" : ""
                } ${
                    attachment.is_internal &&
                    attachment.category !== "requirements_document"
                        ? "border-amber-200 bg-amber-50/70 dark:bg-amber-950/20 dark:border-amber-900/40"
                        : "border-slate-200 bg-slate-50 dark:bg-dark-box dark:border-dark-border"
                }`}
            >
                {versionNumber !== null && (
                    <div
                        className={`w-full text-center py-1 px-3 text-[11px] font-bold tracking-wide select-none border-b ${
                            attachment.is_internal
                                ? "bg-amber-100/90 text-amber-900 dark:bg-amber-900/45 dark:text-amber-200 border-amber-200/80 dark:border-amber-800/40"
                                : isDesign
                                  ? "bg-purple-100/90 text-purple-900 dark:bg-purple-900/45 dark:text-purple-200 border-purple-200/80 dark:border-purple-800/40"
                                  : "bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-300 border-primary/15 dark:border-primary/25"
                        }`}
                    >
                        {t("developments.detail.version_label", {
                            version: versionNumber,
                        })}
                    </div>
                )}

                <div className="flex w-full min-w-0 items-center justify-between p-2.5">
                    <button
                        type="button"
                        onClick={() => handleAttachmentClick(attachment)}
                        className="flex min-w-0 flex-1 items-center gap-2 cursor-pointer select-none rounded-lg text-left outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0 focus-visible:ring-0"
                    >
                        <AttachmentFilePreview
                            developmentId={development.id}
                            attachment={attachment}
                            fallbackIcon={fileIcon.icon}
                            fallbackColor={fileIcon.color}
                        />
                        <div className="min-w-0 flex-1 overflow-hidden">
                            <TruncatedTooltipText
                                text={
                                    attachment.is_internal ? (
                                        <>
                                            {isIt &&
                                            !isClient &&
                                            attachment.category !==
                                                "requirements_document" ? (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setAttachmentToMakePublic(
                                                            attachment
                                                        );
                                                    }}
                                                    className="inline-flex items-center text-amber-700 hover:text-amber-900 dark:text-amber-500 dark:hover:text-amber-300 mr-1 p-0.5 rounded hover:bg-amber-200/60 dark:hover:bg-amber-900/40 cursor-pointer"
                                                    title={t(
                                                        "developments.detail.make_attachment_public"
                                                    )}
                                                >
                                                    <Icon
                                                        value="lock"
                                                        size={12}
                                                    />
                                                </button>
                                            ) : (
                                                <></>
                                            )}
                                            {attachment.name}
                                        </>
                                    ) : (
                                        attachment.name
                                    )
                                }
                                tooltip={
                                    attachment.is_internal
                                        ? attachment.name +
                                          " (" +
                                          t(
                                              "developments.detail.is_internal_file"
                                          ) +
                                          ")"
                                        : attachment.name
                                }
                                className="font-semibold text-slate-800 dark:text-zinc-200 group-hover:text-primary transition-colors"
                            />
                            {(dateLabel || sizeLabel) && (
                                <span
                                    className={`flex items-center gap-1.5 truncate mt-0.5 ${
                                        isRequirementOrDesign
                                            ? "text-xs font-semibold text-slate-700 dark:text-zinc-200"
                                            : "text-[10px] text-slate-500 dark:text-zinc-400"
                                    }`}
                                >
                                    {isRequirementOrDesign && (
                                        <Icon
                                            value="clock"
                                            size={13}
                                            className="shrink-0 text-slate-500 dark:text-zinc-400"
                                        />
                                    )}
                                    {dateLabel && <span>{dateLabel}</span>}
                                    {dateLabel && sizeLabel && (
                                        <span
                                            className={
                                                isRequirementOrDesign
                                                    ? "text-slate-400 dark:text-zinc-500"
                                                    : "opacity-60"
                                            }
                                        >
                                            •
                                        </span>
                                    )}
                                    {sizeLabel && (
                                        <span
                                            className={
                                                isRequirementOrDesign
                                                    ? "text-slate-500 dark:text-zinc-400 font-normal text-[11px]"
                                                    : ""
                                            }
                                        >
                                            {sizeLabel}
                                        </span>
                                    )}
                                </span>
                            )}
                        </div>
                    </button>

                    {hasActions && (
                        <div className="flex shrink-0 items-center gap-1.5 ml-2 relative">
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (activeDropdownId === attachment.id) {
                                        setActiveDropdownId(null);
                                    } else {
                                        const rect =
                                            e.currentTarget.getBoundingClientRect();
                                        const spaceBelow =
                                            window.innerHeight - rect.bottom;
                                        if (spaceBelow < 220) {
                                            setDropdownCoords({
                                                bottom:
                                                    window.innerHeight -
                                                    rect.top +
                                                    4,
                                                right:
                                                    window.innerWidth -
                                                    rect.right,
                                            });
                                        } else {
                                            setDropdownCoords({
                                                top: rect.bottom + 4,
                                                right:
                                                    window.innerWidth -
                                                    rect.right,
                                            });
                                        }
                                        setActiveDropdownId(attachment.id);
                                    }
                                }}
                                className="p-1.5 text-slate-500 hover:text-slate-800 dark:text-zinc-400 dark:hover:text-zinc-200 transition-colors rounded-full hover:bg-slate-100 dark:hover:bg-white/10 cursor-pointer"
                            >
                                <Icon value="moreVertical" size={16} />
                            </button>

                            {activeDropdownId === attachment.id &&
                                dropdownCoords &&
                                createPortal(
                                    <div
                                        className="fixed z-[110000] w-48 rounded-xl border border-slate-200 bg-white p-1 shadow-lg dark:border-white/10 dark:bg-dark-card animate-fade-in"
                                        style={{
                                            top: dropdownCoords.top,
                                            bottom: dropdownCoords.bottom,
                                            right: dropdownCoords.right,
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {!hasDownloadCode && (
                                            <button
                                                type="button"
                                                className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-primary dark:text-zinc-300 dark:hover:bg-white/5"
                                                onClick={() => {
                                                    setActiveDropdownId(null);
                                                    void handleDownloadAttachment(
                                                        attachment
                                                    );
                                                }}
                                            >
                                                <Icon
                                                    value="download"
                                                    size={14}
                                                />
                                                <span>
                                                    {t(
                                                        "developments.detail.download"
                                                    )}
                                                </span>
                                            </button>
                                        )}

                                        {attachment.category ===
                                            "requirements_document" &&
                                            isEditor &&
                                            isIt &&
                                            !isClient &&
                                            onOpenDesignModal &&
                                            isInDesignPhase && (
                                                <button
                                                    type="button"
                                                    className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-primary dark:text-zinc-300 dark:hover:bg-white/5"
                                                    onClick={() => {
                                                        setActiveDropdownId(
                                                            null
                                                        );
                                                        onOpenDesignModal(
                                                            attachment.id
                                                        );
                                                    }}
                                                >
                                                    <Icon
                                                        value="palette"
                                                        size={14}
                                                        className="text-primary"
                                                    />
                                                    <span>
                                                        {t(
                                                            "developments.detail.generate_design_button"
                                                        )}
                                                    </span>
                                                </button>
                                            )}

                                        {hasDownloadCode && (
                                            <button
                                                type="button"
                                                className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-primary dark:text-zinc-300 dark:hover:bg-white/5"
                                                onClick={() => {
                                                    setActiveDropdownId(null);
                                                    handleDownloadReactCode(
                                                        attachment
                                                    );
                                                }}
                                            >
                                                <Icon
                                                    value="code"
                                                    size={14}
                                                    className="text-primary"
                                                />
                                                <span>
                                                    {t(
                                                        "developments.detail.download_code"
                                                    )}
                                                </span>
                                            </button>
                                        )}

                                        {sourceReqDocName &&
                                            attachment.requirement_attachment_id && (
                                                <button
                                                    type="button"
                                                    className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-primary dark:text-zinc-300 dark:hover:bg-white/5"
                                                    onClick={() => {
                                                        setActiveDropdownId(
                                                            null
                                                        );
                                                        setActiveReqDocTab(
                                                            "internal"
                                                        );
                                                        setTimeout(() => {
                                                            const el =
                                                                document.getElementById(
                                                                    `attachment-${attachment.requirement_attachment_id}`
                                                                );
                                                            if (el) {
                                                                el.scrollIntoView(
                                                                    {
                                                                        behavior:
                                                                            "smooth",
                                                                        block: "center",
                                                                    }
                                                                );
                                                                el.classList.add(
                                                                    "animate-pulse",
                                                                    "ring-2",
                                                                    "ring-inset",
                                                                    "ring-primary",
                                                                    "bg-primary/10"
                                                                );
                                                                setTimeout(
                                                                    () =>
                                                                        el.classList.remove(
                                                                            "animate-pulse",
                                                                            "ring-2",
                                                                            "ring-inset",
                                                                            "ring-primary",
                                                                            "bg-primary/10"
                                                                        ),
                                                                    2000
                                                                );
                                                            }
                                                        }, 150);
                                                    }}
                                                >
                                                    <Icon
                                                        value="fileText"
                                                        size={14}
                                                    />
                                                    <span>
                                                        {t(
                                                            "developments.detail.go_to_requirements"
                                                        )}
                                                    </span>
                                                </button>
                                            )}

                                        {(isChatFile ||
                                            attachment.category === "design") &&
                                            messageInfo &&
                                            onNavigateToMessage && (
                                                <button
                                                    type="button"
                                                    className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-primary dark:text-zinc-300 dark:hover:bg-white/5"
                                                    onClick={() => {
                                                        setActiveDropdownId(
                                                            null
                                                        );
                                                        onNavigateToMessage(
                                                            messageInfo.messageId
                                                        );
                                                    }}
                                                >
                                                    <Icon
                                                        value="message"
                                                        size={14}
                                                    />
                                                    <span>
                                                        {t(
                                                            "developments.detail.view_in_chat"
                                                        )}
                                                    </span>
                                                </button>
                                            )}

                                        {attachment.is_internal &&
                                            isIt &&
                                            !isClient &&
                                            attachment.category !==
                                                "requirements_document" && (
                                                <button
                                                    type="button"
                                                    className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-primary dark:text-zinc-300 dark:hover:bg-white/5"
                                                    onClick={() => {
                                                        setActiveDropdownId(
                                                            null
                                                        );
                                                        setAttachmentToMakePublic(
                                                            attachment
                                                        );
                                                    }}
                                                >
                                                    <Icon
                                                        value="lock"
                                                        size={14}
                                                    />
                                                    <span>
                                                        {t(
                                                            "developments.detail.make_attachment_public"
                                                        )}
                                                    </span>
                                                </button>
                                            )}

                                        {hasMakeInternal && (
                                            <button
                                                type="button"
                                                className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-primary dark:text-zinc-300 dark:hover:bg-white/5"
                                                onClick={() => {
                                                    setActiveDropdownId(null);
                                                    setAttachmentToMakeInternal(
                                                        attachment
                                                    );
                                                }}
                                            >
                                                <Icon value="lock" size={14} />
                                                <span>
                                                    {t(
                                                        "developments.detail.make_attachment_internal"
                                                    )}
                                                </span>
                                            </button>
                                        )}

                                        {attachment.category !==
                                            "requirements_document" &&
                                            canManageAttachment(attachment) && (
                                                <button
                                                    type="button"
                                                    className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-primary dark:text-zinc-300 dark:hover:bg-white/5"
                                                    onClick={() => {
                                                        setActiveDropdownId(
                                                            null
                                                        );
                                                        openRenameAttachment(
                                                            attachment
                                                        );
                                                    }}
                                                >
                                                    <Icon
                                                        value="pencil"
                                                        size={14}
                                                    />
                                                    <span>
                                                        {t(
                                                            "developments.detail.edit_attachment"
                                                        )}
                                                    </span>
                                                </button>
                                            )}

                                        {canDeleteAttachment(attachment) && (
                                            <button
                                                type="button"
                                                className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs font-medium text-rose-600 transition-colors hover:bg-rose-50 hover:text-rose-700 dark:text-rose-400 dark:hover:bg-rose-900/20"
                                                onClick={() => {
                                                    setActiveDropdownId(null);
                                                    setAttachmentToDelete(
                                                        attachment
                                                    );
                                                    setDeleteAssociatedReqDoc(
                                                        true
                                                    );
                                                    setDeleteAssociatedDesigns(
                                                        true
                                                    );
                                                    setDeleteAssociatedPairedDesign(
                                                        true
                                                    );
                                                    setDeleteAssociatedReqForDesign(
                                                        false
                                                    );
                                                }}
                                            >
                                                <Icon value="trash" size={14} />
                                                <span>{t("modal.delete")}</span>
                                            </button>
                                        )}
                                    </div>,
                                    document.body
                                )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <>
            <div className="space-y-5">
                <PanelSection
                    icon="paperclip"
                    title={t("developments.detail.attachments_title")}
                    count={nonDesignAttachments.length}
                    isOpen={openSections.attachments}
                    onToggle={() => toggleSection("attachments")}
                    sectionProps={{
                        className:
                            "bg-box rounded-2xl border border-slate-200/80 p-5 shadow-xs space-y-0",
                        onDragEnter: handleDragEnter,
                        onDragOver: handleDragOver,
                        onDragLeave: handleDragLeave,
                        onDrop: handleDrop,
                    }}
                >
                    <div className="relative flex flex-col gap-3">
                        {error && (
                            <div className="rounded-xl bg-red-50 p-2 text-xs text-red-700">
                                {error}
                            </div>
                        )}

                        {nonDesignAttachments.length > 0 && (
                            <SearchInput
                                value={search}
                                onChange={setSearch}
                                placeholder={t(
                                    "developments.detail.attachments_search_placeholder"
                                )}
                                wrapperClassName="px-0.5"
                            />
                        )}

                        <Tabs<"uploaded" | "chat" | "ai">
                            activeTab={activeFileTab}
                            onChange={setActiveFileTab}
                            className="mb-2"
                            size="sm"
                            syncUrl={false}
                            color="white"
                            items={[
                                {
                                    id: "uploaded",
                                    label: t(
                                        "developments.detail.uploaded_files"
                                    ),
                                    icon: <Icon value="UploadCloud" />,
                                },
                                {
                                    id: "chat",
                                    label: t("developments.detail.chat_files"),
                                    icon: <Icon value="message" />,
                                },
                                ...(isClient
                                    ? []
                                    : [
                                          {
                                              id: "ai" as const,
                                              label: t(
                                                  "developments.detail.ai_files"
                                              ),
                                              icon: <Icon value="bot" />,
                                          },
                                      ]),
                            ]}
                        />

                        {isEditor && activeFileTab === "uploaded" ? (
                            <div className="space-y-3 p-3 bg-slate-50/70 dark:bg-dark-subtle/40 rounded-xl border border-slate-200/70 dark:border-white/10">
                                <div>
                                    <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:text-zinc-400 mb-1">
                                        {t("developments.detail.file_type")}
                                    </label>
                                    <Select<string>
                                        value={category}
                                        onChange={(val) => setCategory(val)}
                                        options={fileCategoryOptions}
                                        size="md"
                                        className="w-full"
                                    />
                                </div>

                                {isIt && !isClient && (
                                    <div className="pt-0.5">
                                        <Switch
                                            checked={isInternalFile}
                                            onChange={setIsInternalFile}
                                            label={t(
                                                "developments.detail.is_internal_file"
                                            )}
                                            size="sm"
                                        />
                                    </div>
                                )}

                                <div>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        onChange={handleUpload}
                                        className="hidden"
                                    />
                                    <button
                                        type="button"
                                        onClick={() =>
                                            fileInputRef.current?.click()
                                        }
                                        disabled={uploading}
                                        className="btn primary w-full justify-center h-10 shadow-xs"
                                    >
                                        <Icon value="uploadCloud" size={16} />
                                        <span>
                                            {uploading
                                                ? t(
                                                      "developments.detail.uploading_file"
                                                  )
                                                : t(
                                                      "developments.detail.upload_file"
                                                  )}
                                        </span>
                                    </button>
                                </div>
                            </div>
                        ) : (
                            activeFileTab === "uploaded" && (
                                <p className="text-[11px] text-slate-500 italic bg-slate-50 p-2 rounded-lg border border-slate-200/60">
                                    {t(
                                        "developments.detail.viewer_mode_notice"
                                    )}
                                </p>
                            )
                        )}

                        {groupedAttachments.length > 0 ? (
                            <div className="max-h-[380px] space-y-1 overflow-y-auto pr-0.5">
                                {groupedAttachments.map(([catName, items]) => {
                                    const catOption = fileCategoryOptions.find(
                                        (c) => c.value === catName
                                    );
                                    const catIcon = catOption?.icon || "folder";
                                    const isCategoryOpen =
                                        openCategories[catName] ?? true;

                                    return (
                                        <FileCategoryAccordion
                                            key={catName}
                                            label={catOption?.label ?? catName}
                                            icon={catIcon}
                                            count={items.length}
                                            isOpen={isCategoryOpen}
                                            onToggle={() =>
                                                toggleCategory(catName)
                                            }
                                        >
                                            {items.map((attachment) =>
                                                renderAttachmentRow(attachment)
                                            )}
                                        </FileCategoryAccordion>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="rounded-xl border border-dashed border-slate-200 dark:border-dark-border p-3 text-xs text-slate-400 text-center">
                                {t("developments.detail.attachments_empty")}
                            </p>
                        )}

                        {isEditor && isDragOver && (
                            <div
                                className={`absolute inset-0 z-50 flex items-center justify-center rounded-xl border-2 border-dashed backdrop-blur-sm pointer-events-none transition-all ${
                                    isIt && !isClient && isInternalFile
                                        ? "border-amber-400/80 bg-amber-50/92 dark:bg-amber-950/92"
                                        : "border-primary/70 bg-white/92 dark:bg-dark-box/92"
                                }`}
                            >
                                <div className="flex flex-col items-center gap-2 text-center px-4">
                                    <div
                                        className={`flex items-center gap-2 font-semibold text-sm ${
                                            isIt && !isClient && isInternalFile
                                                ? "text-amber-700 dark:text-amber-300"
                                                : "text-primary dark:text-primary-400"
                                        }`}
                                    >
                                        <Icon value="uploadCloud" size={24} />
                                        <span>
                                            {t(
                                                "developments.detail.drop_files"
                                            )}
                                        </span>
                                    </div>

                                    <div className="flex items-center flex-wrap justify-center gap-1.5">
                                        <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-md bg-slate-100 dark:bg-dark-subtle text-slate-700 dark:text-zinc-300 border border-slate-200 dark:border-dark-border">
                                            <Icon
                                                value={
                                                    fileCategoryOptions.find(
                                                        (o) =>
                                                            o.value === category
                                                    )?.icon ?? "folder"
                                                }
                                                size={12}
                                            />
                                            <span>{selectedCategoryLabel}</span>
                                        </span>

                                        {isIt && !isClient && isInternalFile ? (
                                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md bg-amber-100/90 dark:bg-amber-950 text-amber-800 dark:text-amber-300 border border-amber-300/80 dark:border-amber-800">
                                                <Icon value="lock" size={11} />
                                                <span>
                                                    {t(
                                                        "developments.detail.private_file"
                                                    )}
                                                </span>
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md bg-emerald-100/90 dark:bg-emerald-950 text-emerald-800 dark:text-emerald-300 border border-emerald-300/80 dark:border-emerald-800">
                                                <Icon value="globe" size={11} />
                                                <span>
                                                    {t(
                                                        "developments.detail.public_file"
                                                    )}
                                                </span>
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </PanelSection>

                <PanelSection
                    icon="link"
                    title={t("developments.detail.technical_links_title")}
                    count={technicalLinks.length}
                    isOpen={openSections.technicalLinks}
                    onToggle={() => toggleSection("technicalLinks")}
                >
                    {technicalLinks.length === 0 ? (
                        <p className="rounded-xl border border-dashed border-slate-200 p-2 text-xs text-slate-400 dark:border-dark-border">
                            {t("developments.detail.technical_links_empty")}
                        </p>
                    ) : (
                        <>
                            <SearchInput
                                value={linksSearch}
                                onChange={setLinksSearch}
                                placeholder={t(
                                    "developments.detail.section_search_placeholder"
                                )}
                                wrapperClassName="px-0.5"
                            />
                            <div className="space-y-1.5">
                                {filteredTechnicalLinks.length === 0 ? (
                                    <p className="py-2 text-center text-xs text-slate-400">
                                        {t(
                                            "developments.detail.section_search_empty"
                                        )}
                                    </p>
                                ) : (
                                    filteredTechnicalLinks.map((link) => (
                                        <div
                                            key={link.id}
                                            className={`group flex w-full min-w-0 items-center justify-between rounded-xl border p-2.5 text-left text-xs transition hover:border-primary/60 ${
                                                link.is_internal
                                                    ? "border-amber-200 bg-amber-50/70 dark:bg-amber-950/20 dark:border-amber-900/40"
                                                    : "border-slate-200 bg-slate-50 dark:border-dark-border dark:bg-dark-box"
                                            }`}
                                        >
                                            <a
                                                href={link.url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden rounded-lg outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0 focus-visible:ring-0"
                                            >
                                                <Icon
                                                    value="link"
                                                    size={16}
                                                    className="shrink-0 text-slate-400 transition-colors group-hover:text-primary"
                                                />
                                                <TruncatedTooltipText
                                                    text={
                                                        link.is_internal ? (
                                                            <>
                                                                <Icon
                                                                    value="lock"
                                                                    size={12}
                                                                    className="mb-0.5 mr-0.5 inline text-amber-600 dark:text-amber-400"
                                                                />
                                                                {link.name}
                                                            </>
                                                        ) : (
                                                            link.name
                                                        )
                                                    }
                                                    tooltip={
                                                        link.is_internal
                                                            ? `${link.name} (${t("developments.detail.is_internal_link")})`
                                                            : link.name
                                                    }
                                                    className="font-semibold text-slate-800 transition-colors group-hover:text-primary dark:text-zinc-200"
                                                />
                                            </a>

                                            {canManageLink(link) && (
                                                <div className="ml-2 flex shrink-0 items-center gap-1.5">
                                                    <button
                                                        type="button"
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            openEditTechnicalLink(
                                                                link
                                                            );
                                                        }}
                                                        className="cursor-pointer rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-primary dark:text-zinc-400 dark:hover:bg-white/10 dark:hover:text-primary-400"
                                                        title={t(
                                                            "developments.detail.edit_technical_link"
                                                        )}
                                                    >
                                                        <Icon
                                                            value="pencil"
                                                            size={16}
                                                        />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            setLinkToDelete(
                                                                link
                                                            );
                                                        }}
                                                        className="cursor-pointer rounded-md p-1.5 text-rose-600 transition-colors hover:bg-rose-50 hover:text-rose-700 dark:text-rose-400 dark:hover:bg-rose-950/40 dark:hover:text-rose-300"
                                                        title={t(
                                                            "modal.delete"
                                                        )}
                                                    >
                                                        <Icon
                                                            value="trash"
                                                            size={16}
                                                        />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        </>
                    )}
                    {isEditor && (
                        <button
                            type="button"
                            onClick={openAddTechnicalLink}
                            className="btn-primary w-full"
                        >
                            <Icon value="plus" size={14} />
                            <span>
                                {t("developments.detail.add_technical_link")}
                            </span>
                        </button>
                    )}
                </PanelSection>

                {isIt && !isClient && (
                    <PanelSection
                        icon="lock"
                        iconClassName="text-amber-600"
                        title={t("developments.detail.internal_notes_title")}
                        count={internalNotes.length}
                        isOpen={openSections.internalNotes}
                        onToggle={() => toggleSection("internalNotes")}
                    >
                        {internalNotes.length === 0 ? (
                            <p className="rounded-xl border border-dashed border-slate-200 p-2 text-xs text-slate-400">
                                {t("developments.detail.internal_notes_empty")}
                            </p>
                        ) : (
                            <>
                                <SearchInput
                                    value={notesSearch}
                                    onChange={setNotesSearch}
                                    placeholder={t(
                                        "developments.detail.section_search_placeholder"
                                    )}
                                    wrapperClassName="px-0.5"
                                />
                                <div className="max-h-[220px] space-y-1.5 overflow-y-auto">
                                    {filteredInternalNotes.length === 0 ? (
                                        <p className="py-2 text-center text-xs text-slate-400">
                                            {t(
                                                "developments.detail.section_search_empty"
                                            )}
                                        </p>
                                    ) : (
                                        filteredInternalNotes.map((note) => (
                                            <div
                                                key={note.id}
                                                onClick={() =>
                                                    onNavigateToMessage?.(
                                                        note.id
                                                    )
                                                }
                                                className="group flex w-full min-w-0 items-center justify-between rounded-xl border border-amber-200/80 bg-amber-50/60 p-2.5 text-left text-xs hover:border-amber-400 transition-colors cursor-pointer"
                                            >
                                                <div className="min-w-0 flex-1 overflow-hidden pr-2">
                                                    <span className="block truncate font-semibold text-slate-800 dark:text-zinc-200 group-hover:text-primary transition-colors">
                                                        {getInternalNotePreview(
                                                            note.content
                                                        )}
                                                    </span>
                                                    <span className="block truncate text-[10px] text-slate-500">
                                                        {date(
                                                            note.created_at,
                                                            true,
                                                            true
                                                        )}
                                                    </span>
                                                </div>
                                                {onNavigateToMessage && (
                                                    <button
                                                        type="button"
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            onNavigateToMessage(
                                                                note.id
                                                            );
                                                        }}
                                                        className="cursor-pointer rounded-md p-1.5 text-amber-700 transition-colors hover:bg-amber-100 hover:text-amber-900 dark:text-amber-400 dark:hover:bg-white/10 dark:hover:text-amber-300 shrink-0 ml-1"
                                                        title={t(
                                                            "developments.detail.view_in_chat"
                                                        )}
                                                    >
                                                        <Icon
                                                            value="message"
                                                            size={16}
                                                        />
                                                    </button>
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>
                            </>
                        )}
                    </PanelSection>
                )}

                {/* Participants Section (Viewers & Editors) */}
                {!isClient && (
                    <DevelopmentParticipants
                        developmentId={development.id}
                        participants={development.participants}
                        onUpdate={refresh}
                        isCancelled={isCancelled}
                    />
                )}

                <PanelSection
                    icon="fileText"
                    title={t(
                        "developments.detail.requirements_documents_title"
                    )}
                    count={clientRequirementDocs.length}
                    isOpen={documentsOpen}
                    onToggle={() => setDocumentsOpen((open) => !open)}
                    sectionProps={{
                        className:
                            "bg-box rounded-2xl border border-slate-200/80 p-5 shadow-xs",
                    }}
                >
                    <div className="space-y-1 pt-1">
                        {isEditor && isIt && !isClient && isInPhaseOne && (
                            <button
                                type="button"
                                className="btn primary w-full justify-center gap-2"
                                disabled={generatingDocument}
                                onClick={() => {
                                    void generateDocument();
                                }}
                            >
                                {generatingDocument ? (
                                    <>
                                        <Icon
                                            value="loader"
                                            size={16}
                                            className="animate-spin shrink-0"
                                        />
                                        <span>
                                            {t(
                                                "developments.detail.requirements_documents_generating"
                                            )}
                                        </span>
                                    </>
                                ) : (
                                    <span>
                                        {t(
                                            "developments.detail.requirements_documents_generate"
                                        )}
                                    </span>
                                )}
                            </button>
                        )}
                        {/*
                    <p className="text-xs text-slate-500">
                        {t('developments.detail.requirements_documents_desc')}
                    </p>
                    */}

                        {isIt && !isClient && (
                            <Tabs<"client" | "internal">
                                activeTab={activeReqDocTab}
                                onChange={setActiveReqDocTab}
                                className="mb-2"
                                size="sm"
                                syncUrl={false}
                                color="white"
                                items={[
                                    {
                                        id: "client",
                                        label: t(
                                            "developments.detail.requirements_documents_tab_client"
                                        ),
                                        icon: <Icon value="user" />,
                                        badge: clientRequirementDocs.length,
                                    },
                                    {
                                        id: "internal",
                                        label: t(
                                            "developments.detail.requirements_documents_tab_internal"
                                        ),
                                        icon: <Icon value="lock" />,
                                        badge: internalRequirementDocs.length,
                                    },
                                ]}
                            />
                        )}

                        {documentError && (
                            <p role="alert" className="text-xs text-red-600">
                                {documentError}
                            </p>
                        )}

                        {(!isClient && activeReqDocTab === "internal"
                            ? internalRequirementDocs
                            : clientRequirementDocs
                        ).length > 0 ? (
                            (!isClient && activeReqDocTab === "internal"
                                ? internalRequirementDocs
                                : clientRequirementDocs
                            ).map((attachment) =>
                                renderAttachmentRow(attachment)
                            )
                        ) : (
                            <p className="py-2 text-center text-xs text-slate-400 dark:text-zinc-500">
                                {!isClient && activeReqDocTab === "internal"
                                    ? t(
                                          "developments.detail.requirements_documents_empty_internal"
                                      )
                                    : t(
                                          "developments.detail.requirements_documents_empty_client"
                                      )}
                            </p>
                        )}
                    </div>
                </PanelSection>

                {(isInDesignPhase ||
                    designFiles.length > 0 ||
                    designLinks.length > 0) && (
                    <PanelSection
                        icon="layers"
                        title={t("developments.detail.design_title")}
                        count={designFiles.length + designLinks.length}
                        isOpen={designOpen}
                        onToggle={() => setDesignOpen((open) => !open)}
                        sectionProps={{
                            className:
                                "bg-box rounded-2xl border border-slate-200/80 p-5 shadow-xs",
                        }}
                    >
                        <div className="space-y-1 pt-1">
                            {isEditor &&
                                isIt &&
                                !isClient &&
                                onOpenDesignModal &&
                                isInDesignPhase && (
                                    <button
                                        type="button"
                                        className="btn primary w-full justify-center gap-2 mb-2"
                                        onClick={() => onOpenDesignModal()}
                                    >
                                        <Icon value="palette" size={16} />
                                        <span>
                                            {t(
                                                "developments.detail.generate_design_button"
                                            )}
                                        </span>
                                    </button>
                                )}
                            <Tabs<"files" | "links">
                                activeTab={activeDesignTab}
                                onChange={setActiveDesignTab}
                                className="mb-2"
                                size="sm"
                                syncUrl={false}
                                color="white"
                                items={[
                                    {
                                        id: "files",
                                        label: t(
                                            "developments.detail.attachments_title"
                                        ),
                                        icon: <Icon value="pdf" />,
                                    },
                                    {
                                        id: "links",
                                        label: t(
                                            "developments.detail.technical_links_title"
                                        ),
                                        icon: <Icon value="link" />,
                                    },
                                ]}
                            />

                            <div className="space-y-1">
                                {activeDesignTab === "files" ? (
                                    designFiles.length > 0 ? (
                                        designFiles.map((attachment) =>
                                            renderAttachmentRow(attachment)
                                        )
                                    ) : (
                                        <p className="py-2 text-center text-xs text-slate-400 dark:text-zinc-500">
                                            {t(
                                                "developments.detail.design_files_empty"
                                            )}
                                        </p>
                                    )
                                ) : designLinks.length > 0 ? (
                                    <div className="space-y-2">
                                        {designLinks.map((link) => (
                                            <a
                                                key={link.id}
                                                href={link.url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="flex items-center gap-2 p-2 rounded-xl border border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors group"
                                            >
                                                <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 flex items-center justify-center shrink-0">
                                                    <Icon
                                                        value="link"
                                                        size={16}
                                                    />
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm font-semibold text-slate-900 dark:text-white truncate group-hover:text-primary transition-colors">
                                                        {link.name}
                                                    </p>
                                                    <p className="text-xs text-slate-500 truncate">
                                                        {link.url}
                                                    </p>
                                                </div>
                                            </a>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="py-2 text-center text-xs text-slate-400 dark:text-zinc-500">
                                        {t(
                                            "developments.detail.design_links_empty"
                                        )}
                                    </p>
                                )}
                            </div>
                        </div>
                    </PanelSection>
                )}

                {(isInFrontendPhase || frontendFiles.length > 0) && (
                    <PanelSection
                        icon="monitor"
                        title={t("developments.detail.frontend_title")}
                        count={frontendFiles.length}
                        isOpen={frontendOpen}
                        onToggle={() => setFrontendOpen((open) => !open)}
                        sectionProps={{
                            className:
                                "bg-box rounded-2xl border border-slate-200/80 p-5 shadow-xs",
                        }}
                    >
                        <div className="space-y-1 pt-1">
                            <div className="space-y-1">
                                {frontendFiles.length > 0 ? (
                                    frontendFiles.map((attachment) =>
                                        renderAttachmentRow(attachment)
                                    )
                                ) : (
                                    <p className="py-2 text-center text-xs text-slate-400 dark:text-zinc-500">
                                        {t(
                                            "developments.detail.frontend_files_empty"
                                        )}
                                    </p>
                                )}
                            </div>
                        </div>
                    </PanelSection>
                )}

                {(isInBackendPhase || backendFiles.length > 0) && (
                    <PanelSection
                        icon="server"
                        title={t("developments.detail.backend_title")}
                        count={backendFiles.length}
                        isOpen={backendOpen}
                        onToggle={() => setBackendOpen((open) => !open)}
                        sectionProps={{
                            className:
                                "bg-box rounded-2xl border border-slate-200/80 p-5 shadow-xs",
                        }}
                    >
                        <div className="space-y-1 pt-1">
                            <div className="space-y-1">
                                {backendFiles.length > 0 ? (
                                    backendFiles.map((attachment) =>
                                        renderAttachmentRow(attachment)
                                    )
                                ) : (
                                    <p className="py-2 text-center text-xs text-slate-400 dark:text-zinc-500">
                                        {t(
                                            "developments.detail.backend_files_empty"
                                        )}
                                    </p>
                                )}
                            </div>
                        </div>
                    </PanelSection>
                )}

                {/* Platform tasks */}
                {isInTasksPhaseOrHigher && tickets.length > 0 && (
                    <TicketsSection
                        tickets={tickets}
                        onDelete={handleDeleteTicket}
                        canAdd={false}
                        canDelete={isEditor}
                    />
                )}

                {isInTasksPhaseOrHigher && isIt && (
                    <PanelSection
                        icon="layoutList"
                        iconClassName="text-emerald-500"
                        title={t("developments.detail.internal_tasks")}
                        count={development.tasks?.length || 0}
                        isOpen={openSections.internalTasks}
                        onToggle={() => toggleSection("internalTasks")}
                    >
                        {isEditor && (
                            <div className="flex gap-2 pt-1 mb-2">
                                <button
                                    type="button"
                                    onClick={() =>
                                        setIsSuggestTasksModalOpen(true)
                                    }
                                    className="btn primary flex-1 justify-center gap-2"
                                >
                                    <Icon value="sparkles" size={16} />
                                    <span>
                                        {t(
                                            "developments.detail.suggest_tasks_ai"
                                        )}
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    onClick={handleOpenNewTask}
                                    className="btn secondary flex-1 justify-center gap-2"
                                >
                                    <Icon value="plus" size={16} />
                                    <span>{t("tasks.new_task")}</span>
                                </button>
                            </div>
                        )}

                        {development.tasks && development.tasks.length > 0 ? (
                            <div className="space-y-2 mt-3">
                                {development.tasks.map((task: any) => (
                                    <a
                                        key={task.id}
                                        href={`/tasks/${task.id}`}
                                        className="flex flex-col px-3 py-2 rounded-xl border border-slate-200 dark:border-zinc-700 hover:border-primary transition-colors bg-white dark:bg-zinc-900 cursor-pointer group"
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex-1 min-w-0 truncate">
                                                <span className="text-sm font-semibold text-slate-800 dark:text-zinc-200 truncate group-hover:text-primary transition-colors">
                                                    {task.code} - {task.title}
                                                </span>
                                            </div>

                                            {/* Task Actions */}
                                            <div className="shrink-0 relative">
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        if (
                                                            activeTaskDropdownId ===
                                                            task.id
                                                        ) {
                                                            setActiveTaskDropdownId(
                                                                null
                                                            );
                                                        } else {
                                                            const rect =
                                                                e.currentTarget.getBoundingClientRect();
                                                            const spaceBelow =
                                                                window.innerHeight -
                                                                rect.bottom;
                                                            if (
                                                                spaceBelow < 220
                                                            ) {
                                                                setDropdownCoords(
                                                                    {
                                                                        bottom:
                                                                            window.innerHeight -
                                                                            rect.top +
                                                                            4,
                                                                        right:
                                                                            window.innerWidth -
                                                                            rect.right,
                                                                    }
                                                                );
                                                            } else {
                                                                setDropdownCoords(
                                                                    {
                                                                        top:
                                                                            rect.bottom +
                                                                            4,
                                                                        right:
                                                                            window.innerWidth -
                                                                            rect.right,
                                                                    }
                                                                );
                                                            }
                                                            setActiveTaskDropdownId(
                                                                task.id
                                                            );
                                                        }
                                                    }}
                                                    className="p-1 text-slate-400 hover:text-slate-600 dark:text-zinc-500 dark:hover:text-zinc-300 transition-colors rounded hover:bg-slate-100 dark:hover:bg-white/10 cursor-pointer"
                                                >
                                                    <Icon
                                                        value="moreVertical"
                                                        size={16}
                                                    />
                                                </button>

                                                {activeTaskDropdownId ===
                                                    task.id &&
                                                    dropdownCoords &&
                                                    createPortal(
                                                        <div
                                                            className="fixed z-[110000] w-40 rounded-xl border border-slate-200 bg-white p-1 shadow-lg dark:border-white/10 dark:bg-dark-card animate-fade-in"
                                                            style={{
                                                                top: dropdownCoords.top,
                                                                bottom: dropdownCoords.bottom,
                                                                right: dropdownCoords.right,
                                                            }}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                            }}
                                                        >
                                                            <button
                                                                type="button"
                                                                className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-primary dark:text-zinc-300 dark:hover:bg-white/5"
                                                                onClick={() => {
                                                                    setActiveTaskDropdownId(
                                                                        null
                                                                    );
                                                                    handleCopyTask(
                                                                        task
                                                                    );
                                                                }}
                                                            >
                                                                <Icon
                                                                    value="copy"
                                                                    size={14}
                                                                />
                                                                <span>
                                                                    {t(
                                                                        "developments.detail.copy"
                                                                    )}
                                                                </span>
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-primary dark:text-zinc-300 dark:hover:bg-white/5"
                                                                onClick={() => {
                                                                    setActiveTaskDropdownId(
                                                                        null
                                                                    );
                                                                    handleDownloadTask(
                                                                        task
                                                                    );
                                                                }}
                                                            >
                                                                <Icon
                                                                    value="download"
                                                                    size={14}
                                                                />
                                                                <span>
                                                                    {t(
                                                                        "developments.detail.download"
                                                                    )}
                                                                </span>
                                                            </button>
                                                        </div>,
                                                        document.body
                                                    )}
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setStatusTarget(task);
                                                }}
                                            >
                                                <Badge
                                                    value={
                                                        t(
                                                            `tasks.statuses.${task.status?.tag}`
                                                        ) || task.status?.name
                                                    }
                                                    variant={
                                                        TASK_STATUS_VARIANT[
                                                            task.status?.tag ??
                                                                ""
                                                        ] ?? "neutral"
                                                    }
                                                />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setAssigneeTarget(task);
                                                }}
                                                className="cursor-pointer rounded-full transition-transform hover:scale-110 hover:ring-2 hover:ring-primary/40"
                                            >
                                                {task.assignee ? (
                                                    <Tooltip
                                                        title={
                                                            task.assignee.name
                                                        }
                                                        placement="top"
                                                    >
                                                        {task.assignee.avatar ||
                                                        task.assignee.photo ? (
                                                            <img
                                                                src={
                                                                    task
                                                                        .assignee
                                                                        .avatar ||
                                                                    task
                                                                        .assignee
                                                                        .photo
                                                                }
                                                                alt={
                                                                    task
                                                                        .assignee
                                                                        .name
                                                                }
                                                                className="w-6 h-6 rounded-full object-cover shrink-0 ring-1 ring-slate-200 dark:ring-zinc-700"
                                                            />
                                                        ) : (
                                                            <div className="avatar">
                                                                {getInitials(
                                                                    task
                                                                        .assignee
                                                                        .name
                                                                )}
                                                            </div>
                                                        )}
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip
                                                        title={t(
                                                            "tasks.assignee.unassigned"
                                                        )}
                                                        placement="top"
                                                    >
                                                        <div className="avatar-unassigned">
                                                            <Icon
                                                                value="user"
                                                                size={12}
                                                            />
                                                        </div>
                                                    </Tooltip>
                                                )}
                                            </button>
                                        </div>
                                    </a>
                                ))}
                            </div>
                        ) : (
                            <p className="rounded-xl border border-dashed border-slate-200 p-4 text-center text-xs text-slate-400 mt-2">
                                {t("developments.detail.internal_tasks_empty")}
                            </p>
                        )}
                    </PanelSection>
                )}

                <Modal
                    isOpen={isLinkModalOpen}
                    onClose={() => setIsLinkModalOpen(false)}
                    title={
                        editingLinkId
                            ? t("developments.detail.edit_technical_link")
                            : t("developments.detail.add_technical_link")
                    }
                    onSubmit={saveTechnicalLink}
                    isSubmitting={isSavingLink}
                    submitLabel={t("modal.save")}
                    submitDisabled={!linkFormName.trim() || !linkFormUrl.trim()}
                >
                    <div className="space-y-4">
                        <Input
                            required
                            label={t("developments.detail.technical_link_name")}
                            value={linkFormName}
                            onChange={(event) =>
                                setLinkFormName(event.target.value)
                            }
                            placeholder={t(
                                "developments.detail.technical_link_name_placeholder"
                            )}
                        />
                        <Input
                            required
                            type="url"
                            label={t("developments.detail.technical_link_url")}
                            value={linkFormUrl}
                            onChange={(event) =>
                                setLinkFormUrl(event.target.value)
                            }
                            placeholder="https://..."
                        />
                        {isIt && !isClient && (
                            <Alert variant="warning">
                                <Switch
                                    checked={linkFormIsInternal}
                                    onChange={setLinkFormIsInternal}
                                    label={t(
                                        "developments.detail.is_internal_link"
                                    )}
                                    color="warning"
                                />
                            </Alert>
                        )}
                    </div>
                </Modal>

                <Modal
                    isOpen={Boolean(linkToDelete)}
                    onClose={() => {
                        if (!isDeletingLink) {
                            setLinkToDelete(null);
                        }
                    }}
                    title={t("developments.detail.delete_technical_link_title")}
                    maxWidth="sm"
                    submitVariant="danger"
                    onSubmit={deleteTechnicalLink}
                    isSubmitting={isDeletingLink}
                    submitLabel={t("modal.delete")}
                    submittingLabel={t("modal.deleting")}
                    cancelLabel={t("modal.cancel")}
                >
                    <div className="space-y-3 py-2 text-center">
                        <p className="text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                            {t(
                                "developments.detail.delete_technical_link_desc"
                            )}
                        </p>
                        <div className="space-y-1 min-w-0 px-2">
                            <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                                {linkToDelete?.name}
                            </p>
                            {linkToDelete?.url && (
                                <a
                                    href={linkToDelete.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="block truncate text-xs text-primary hover:underline dark:text-primary-400"
                                >
                                    {linkToDelete.url}
                                </a>
                            )}
                        </div>
                    </div>
                </Modal>

                {/* Fullscreen Image Lightbox */}
                <ImageLightboxModal
                    isOpen={isLightboxOpen}
                    items={lightboxItems}
                    initialIndex={lightboxIndex}
                    initialId={lightboxId}
                    onClose={() => setIsLightboxOpen(false)}
                    onDelete={(item) => {
                        const attId =
                            typeof item.id === "number"
                                ? item.id
                                : Number(item.id);
                        if (attId) {
                            void deleteAttachmentDirect(attId);
                        }
                    }}
                    headerActions={(item) => {
                        const att = imageAttachments.find(
                            (a) => String(a.id) === String(item.id)
                        );
                        if (att?.requirement_attachment_id) {
                            return (
                                <button
                                    type="button"
                                    className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer flex items-center gap-1.5"
                                    title={t(
                                        "developments.detail.go_to_requirements"
                                    )}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const reqDoc = (
                                            development.attachments ?? []
                                        ).find(
                                            (a) =>
                                                a.id ===
                                                att.requirement_attachment_id
                                        );
                                        if (reqDoc)
                                            setReqDocViewerAttachment(reqDoc);

                                        setActiveReqDocTab("internal");
                                        setTimeout(() => {
                                            const el = document.getElementById(
                                                `attachment-${att.requirement_attachment_id}`
                                            );
                                            if (el) {
                                                el.scrollIntoView({
                                                    behavior: "smooth",
                                                    block: "center",
                                                });
                                                el.classList.add(
                                                    "animate-pulse",
                                                    "!border-primary",
                                                    "!bg-primary/10"
                                                );
                                                setTimeout(
                                                    () =>
                                                        el.classList.remove(
                                                            "animate-pulse",
                                                            "!border-primary",
                                                            "!bg-primary/10"
                                                        ),
                                                    2000
                                                );
                                            }
                                        }, 150);
                                    }}
                                >
                                    <Icon
                                        value="fileText"
                                        size={16}
                                        className="text-white"
                                    />
                                </button>
                            );
                        }
                        return null;
                    }}
                />

                <FileViewer
                    isOpen={Boolean(fileViewerAttachment)}
                    onClose={() => setFileViewerAttachment(null)}
                    title={fileViewerAttachment?.name ?? ""}
                    filename={fileViewerAttachment?.name ?? ""}
                    mimeType={fileViewerAttachment?.mime_type}
                    fileSize={fileViewerAttachment?.file_size}
                    fileDate={
                        fileViewerAttachment?.created_at ||
                        (fileViewerAttachment
                            ? attachmentMessageInfo.get(fileViewerAttachment.id)
                                  ?.createdAt
                            : undefined)
                    }
                    directUrl={
                        fileViewerAttachment?.category === "design" &&
                        (fileViewerAttachment as any).file_path
                            ? (fileViewerAttachment as any).file_path
                            : undefined
                    }
                    loadBlob={() => {
                        if (!fileViewerAttachment) {
                            return Promise.reject(
                                new Error("Missing attachment")
                            );
                        }

                        return developmentApi.getAttachmentBlob(
                            development.id,
                            fileViewerAttachment.id
                        );
                    }}
                    allowDownload={true}
                    /*bodyTopContent={
                    fileViewerAttachment?.requirement_attachment_id ? (
                        <button
                            type="button"
                            className="btn primary btn-sm flex items-center gap-1.5 shadow-sm my-2"
                            onClick={() => {
                                const reqDoc = (development.attachments ?? []).find(a => a.id === fileViewerAttachment.requirement_attachment_id);
                                if(reqDoc) setReqDocViewerAttachment(reqDoc);

                                setActiveReqDocTab('internal');
                                setTimeout(() => {
                                    const el = document.getElementById(`attachment-${fileViewerAttachment.requirement_attachment_id}`);
                                    if(el){
                                        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                        el.classList.add('animate-pulse', '!border-primary', '!bg-primary/10');
                                        setTimeout(() => el.classList.remove('animate-pulse', '!border-primary', '!bg-primary/10'), 2000);
                                    }
                                }, 150);
                            }}
                        >
                            <Icon value="fileText" size={14} />
                            <span>{t('developments.detail.go_to_requirements')}</span>
                        </button>
                    ) : null
                }*/
                />

                <FileViewer
                    isOpen={Boolean(reqDocViewerAttachment)}
                    onClose={() => setReqDocViewerAttachment(null)}
                    title={reqDocViewerAttachment?.name ?? ""}
                    filename={reqDocViewerAttachment?.name ?? ""}
                    mimeType={reqDocViewerAttachment?.mime_type}
                    fileSize={reqDocViewerAttachment?.file_size}
                    fileDate={
                        reqDocViewerAttachment?.created_at ||
                        (reqDocViewerAttachment
                            ? attachmentMessageInfo.get(
                                  reqDocViewerAttachment.id
                              )?.createdAt
                            : undefined)
                    }
                    loadBlob={() => {
                        if (!reqDocViewerAttachment) {
                            return Promise.reject(
                                new Error("Missing attachment")
                            );
                        }
                        return developmentApi.getAttachmentBlob(
                            development.id,
                            reqDocViewerAttachment.id
                        );
                    }}
                    allowDownload={true}
                    zIndex={120000}
                />

                <Modal
                    isOpen={Boolean(attachmentToRename)}
                    onClose={() => {
                        if (!isRenamingAttachment) {
                            setAttachmentToRename(null);
                            setRenameFormName("");
                        }
                    }}
                    title={t("developments.detail.edit_attachment")}
                    maxWidth="sm"
                    onSubmit={handleRenameAttachment}
                    isSubmitting={isRenamingAttachment}
                    submitLabel={t("modal.save")}
                    submittingLabel={t("modal.saving")}
                    cancelLabel={t("modal.cancel")}
                >
                    <div className="py-1">
                        <Input
                            required
                            label={t("developments.detail.attachment_name")}
                            value={renameFormName}
                            onChange={(event) =>
                                setRenameFormName(event.target.value)
                            }
                            placeholder={t(
                                "developments.detail.attachment_name_placeholder"
                            )}
                        />
                    </div>
                </Modal>

                {/* Confirm Delete Attachment Modal */}
                {Boolean(attachmentToDelete) && (
                    <Modal
                        isOpen={Boolean(attachmentToDelete)}
                        onClose={() => {
                            if (!isDeletingAttachment) {
                                setAttachmentToDelete(null);
                            }
                        }}
                        title={deleteModalTitle}
                        maxWidth="md"
                        submitVariant="danger"
                        onSubmit={handleConfirmDeleteAttachment}
                        isSubmitting={isDeletingAttachment}
                        submitLabel={t("modal.delete")}
                        submittingLabel={t("modal.deleting")}
                        cancelLabel={t("modal.cancel")}
                        hideFullscreen={true}
                    >
                        <div>
                            <div className="text-center text-sm">
                                {deleteModalDesc}
                            </div>
                            <div className="text-center mt-2 font-bold text-sm">
                                {attachmentToDelete?.name}
                            </div>

                            {attachmentToDelete?.category ===
                                "requirements_document" && (
                                <div className="space-y-3 mt-6">
                                    {associatedReqDoc && (
                                        <Alert variant="error">
                                            <Switch
                                                checked={deleteAssociatedReqDoc}
                                                onChange={
                                                    setDeleteAssociatedReqDoc
                                                }
                                                html={
                                                    t(
                                                        "developments.detail.delete_associated_req_doc"
                                                    ) +
                                                    '<br/><span class="font-bold">' +
                                                    associatedReqDoc.name +
                                                    "</span>"
                                                }
                                            />
                                        </Alert>
                                    )}
                                    {associatedDesignsForReq.length > 0 && (
                                        <Alert variant="error">
                                            <Switch
                                                checked={
                                                    deleteAssociatedDesigns
                                                }
                                                onChange={
                                                    setDeleteAssociatedDesigns
                                                }
                                                html={
                                                    t(
                                                        "developments.detail.delete_associated_designs"
                                                    ) +
                                                    '<br/><span class="font-bold text-xs">' +
                                                    associatedDesignsForReq
                                                        .map(
                                                            (d: Attachment) =>
                                                                d.name
                                                        )
                                                        .join("<br/>") +
                                                    "</span>"
                                                }
                                            />
                                        </Alert>
                                    )}
                                </div>
                            )}

                            {attachmentToDelete?.category === "design" && (
                                <div className="space-y-3 mt-6">
                                    {associatedPairedDesign && (
                                        <Alert variant="error">
                                            <Switch
                                                checked={
                                                    deleteAssociatedPairedDesign
                                                }
                                                onChange={
                                                    setDeleteAssociatedPairedDesign
                                                }
                                                html={
                                                    t(
                                                        "developments.detail.delete_associated_paired_design"
                                                    ) +
                                                    '<br/><span class="font-bold">' +
                                                    associatedPairedDesign.name +
                                                    "</span>"
                                                }
                                            />
                                        </Alert>
                                    )}
                                    {associatedReqsForDesign.length > 0 && (
                                        <Alert variant="error">
                                            <Switch
                                                checked={
                                                    deleteAssociatedReqForDesign
                                                }
                                                onChange={
                                                    setDeleteAssociatedReqForDesign
                                                }
                                                html={
                                                    t(
                                                        "developments.detail.delete_associated_reqs_from_design"
                                                    ) +
                                                    '<br/><span class="font-bold text-xs">' +
                                                    associatedReqsForDesign
                                                        .map(
                                                            (r: Attachment) =>
                                                                r.name
                                                        )
                                                        .join("<br/>") +
                                                    "</span>"
                                                }
                                            />
                                        </Alert>
                                    )}
                                </div>
                            )}
                        </div>
                    </Modal>
                )}

                {/* Confirm Make Attachment Public Modal */}
                <Modal
                    isOpen={Boolean(attachmentToMakePublic)}
                    onClose={() => {
                        if (!isMakingPublic) {
                            setAttachmentToMakePublic(null);
                        }
                    }}
                    title={t(
                        "developments.detail.make_attachment_public_title"
                    )}
                    maxWidth="sm"
                    submitVariant="primary"
                    onSubmit={handleConfirmMakePublic}
                    isSubmitting={isMakingPublic}
                    submitLabel={t(
                        "developments.detail.make_attachment_public_confirm"
                    )}
                    submittingLabel={t(
                        "developments.detail.making_attachment_public"
                    )}
                    cancelLabel={t("modal.cancel")}
                >
                    <div className="space-y-3 py-2 text-center">
                        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400">
                            <Icon value="lock" size={24} />
                        </div>
                        <p className="text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                            {t(
                                "developments.detail.make_attachment_public_desc"
                            )}
                        </p>
                        <p className="text-sm font-semibold text-slate-900 dark:text-white px-2">
                            {attachmentToMakePublic?.name}
                        </p>
                    </div>
                </Modal>

                {/* Confirm Make Attachment Internal Modal */}
                <Modal
                    isOpen={Boolean(attachmentToMakeInternal)}
                    onClose={() => {
                        if (!isMakingInternal) {
                            setAttachmentToMakeInternal(null);
                        }
                    }}
                    title={t(
                        "developments.detail.make_attachment_internal_title"
                    )}
                    maxWidth="sm"
                    submitVariant="primary"
                    onSubmit={handleConfirmMakeInternal}
                    isSubmitting={isMakingInternal}
                    submitLabel={t(
                        "developments.detail.make_attachment_internal_confirm"
                    )}
                    submittingLabel={t(
                        "developments.detail.making_attachment_internal"
                    )}
                    cancelLabel={t("modal.cancel")}
                >
                    <div className="space-y-3 py-2 text-center">
                        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                            <Icon value="lock" size={24} />
                        </div>
                        <p className="text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                            {t(
                                "developments.detail.make_attachment_internal_desc"
                            )}
                        </p>
                        <p className="text-sm font-semibold text-slate-900 dark:text-white px-2">
                            {attachmentToMakeInternal?.name}
                        </p>
                    </div>
                </Modal>

                {isSuggestTasksModalOpen && (
                    <SuggestTasksModal
                        isOpen={isSuggestTasksModalOpen}
                        onClose={() => setIsSuggestTasksModalOpen(false)}
                        developmentId={development.id}
                        onSuccess={() => {
                            setIsSuggestTasksModalOpen(false);
                            refresh();
                        }}
                    />
                )}

                <NewTaskModal
                    isOpen={isNewTaskModalOpen}
                    onClose={() => setIsNewTaskModalOpen(false)}
                    onSuccess={() => {
                        setIsNewTaskModalOpen(false);
                        refresh();
                    }}
                    statuses={taskStatuses}
                    prefilledDevelopmentId={development.id}
                />

                {statusTarget && (
                    <ChangeTaskStatusModal
                        isOpen={statusTarget !== null}
                        task={statusTarget}
                        onClose={() => setStatusTarget(null)}
                        onSuccess={(updatedTask: any) => {
                            setStatusTarget(null);
                            const updatedDev = {
                                ...development,
                                tasks: (development.tasks ?? []).map(
                                    (tk: any) =>
                                        tk.id === updatedTask.id
                                            ? { ...tk, ...updatedTask }
                                            : tk
                                ),
                            };
                            onUpdate(updatedDev);
                        }}
                    />
                )}

                {assigneeTarget && (
                    <ChangeTaskAssigneeModal
                        isOpen={assigneeTarget !== null}
                        task={assigneeTarget}
                        onClose={() => setAssigneeTarget(null)}
                        onSuccess={(updatedTask: any) => {
                            setAssigneeTarget(null);
                            const updatedDev = {
                                ...development,
                                tasks: (development.tasks ?? []).map(
                                    (tk: any) =>
                                        tk.id === updatedTask.id
                                            ? { ...tk, ...updatedTask }
                                            : tk
                                ),
                            };
                            onUpdate(updatedDev);
                        }}
                    />
                )}
            </div>
        </>
    );
};

export default DeliverablesPanel;
